// src/api/entities.js
// Real-data entities for Privana Admin/Club. No stubs.
// Includes: User, AuditLog, Member, Event, TeeTimeBooking, Invoice, GolfCourse, AIChat,
//           UserProfile, ClubUser, Message, SentimentLog, RevenueAlert, Staff, BirthdayReminder,
//           ClubFacilityConfig (+ re-export Club) and many more used across the app.

import { apiGet, apiPost, apiPatch, apiDelete, apiPut, apiStartMicrosoftOAuth, getCurrentUser, asArray } from "./apiClient";
import { buildClubUserListPaths, buildClubUserUserPaths } from "./clubUsers";
import { normalizePaths, tryRequests } from "./pathUtils";
import { isPlatformContext } from "@/utils/platformAccess";
import fetchCurrentUser, { extractClubCodeFromPath } from "@/utils/fetchCurrentUser.js";
import { getPlatformRoleHints, shouldSkipPlatformProfile } from "@/utils/platformProfile.js";

/* ------------------------ helpers ------------------------ */
function pickFirst(...vals) { for (const v of vals) if (v !== undefined && v !== null) return v; return null; }
function normalizeEntityList(value, entityName) {
  if (value?.ok === false) {
    throw new Error(value.error || `${entityName} request failed`);
  }
  const list = asArray(value);
  if (list.length || value == null || Array.isArray(value) || Array.isArray(value?.data) || Array.isArray(value?.data?.items) || Array.isArray(value?.items) || Array.isArray(value?.results)) {
    return list;
  }
  console.warn(`Unexpected list response for ${entityName}; returning empty array.`);
  return [];
}

function normalizeStrictEntityList(value, entityName) {
  if (value?.ok === false) {
    const error = new Error(value.error?.message || value.error || `${entityName} request failed`);
    error.code = value.error?.code || `HTTP_${value.status || 500}`;
    error.status = value.status;
    throw error;
  }
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.data?.items)) return value.data.items;
  if (Array.isArray(value?.items)) return value.items;
  throw Object.assign(new Error(`Unexpected list response for ${entityName}`), { code: `${entityName.toUpperCase()}_INVALID_RESPONSE` });
}

function addQuery(path, params = {}) {
  const keys = Object.keys(params || {});
  if (!keys.length) return path;
  const qs = new URLSearchParams(
    Object.entries(params).reduce((acc, [k, v]) => {
      if (v === undefined || v === null || v === "") return acc;
      acc[k] = Array.isArray(v) ? v.join(",") : String(v);
      return acc;
    }, {})
  ).toString();
  return `${path}${path.includes("?") ? "&" : "?"}${qs}`;
}

function clubizePath(path, clubRef) {
  if (!path || !clubRef) return null;
  if (path.startsWith("/api/admin/")) return `/api/admin/clubs/${clubRef}${path.substring("/api/admin".length)}`;
  if (path.startsWith("/api/")) return `/api/clubs/${clubRef}${path.substring("/api".length)}`;
  return null;
}

function unwrap(res) {
  if (!res) return res;
  if (Object.prototype.hasOwnProperty.call(res, "ok")) {
    if (res.data !== undefined && res.data !== null) return res.data;
    return res;
  }
  return res;
}

/** Try multiple endpoint shapes until one works. */
function getActiveClubCode() {
  if (typeof window === "undefined") return null;
  try {
    return extractClubCodeFromPath(window.location?.pathname || "");
  } catch {
    return null;
  }
}

function isClubPortalPathname(pathname) {
  if (typeof pathname !== "string") return false;
  if (extractClubCodeFromPath(pathname)) return true;
  return /^\/\d+(?:\/|$)/.test(pathname);
}

export function orderPathsForClub(paths = [], clubCode) {
  const pathList = normalizePaths(paths);

  if (!clubCode) return pathList;

  const pathname = typeof window !== "undefined" ? window.location?.pathname || "" : "";
  const isClubPortal = isClubPortalPathname(pathname);
  const isPlatform = isPlatformContext();
  const { platformRole } = getPlatformRoleHints();
  const isClubUser = platformRole === "club_user";

  const publicClubPaths = [];
  const adminClubPaths = [];

  pathList.forEach((path) => {
    if (path.includes("/api/clubs/") || path.includes("/api/admin/clubs/")) {
      publicClubPaths.push(path);
      return;
    }

    const clubPath = path && clubizePath(path, clubCode);
    if (!clubPath) return;
    if (path.startsWith("/api/admin/")) {
      adminClubPaths.push(clubPath);
    } else {
      publicClubPaths.push(clubPath);
    }
  });

  const ordered = [...new Set([...publicClubPaths, ...adminClubPaths, ...pathList])];

  if (isClubPortal) {
    return normalizePaths(ordered).filter((path) => !path?.startsWith("/api/admin/"));
  }

  if (!isPlatform) {
    return normalizePaths(ordered).filter((path) => !path?.startsWith("/api/admin/"));
  }

  if (!isClubUser) return ordered;

  // Club users should never hit non-club-scoped admin endpoints
  return normalizePaths(ordered).filter((path) => {
    if (!path?.startsWith("/api/admin/")) return true;
    return path.includes("/api/admin/clubs/");
  });
}

function poloClubPath(segment, clubCode = getActiveClubCode()) {
  if (!clubCode) return null;
  return `/api/clubs/${encodeURIComponent(String(clubCode))}${segment}`;
}

async function tryGet(paths, params) {
  const clubOrderedPaths = orderPathsForClub(paths, getActiveClubCode());
  return tryRequests(clubOrderedPaths, (p) => apiGet(addQuery(p, params)));
}
async function tryPost(paths, body) {
  const clubOrderedPaths = orderPathsForClub(paths, getActiveClubCode());
  return tryRequests(clubOrderedPaths, (p) => apiPost(p, body));
}
async function tryPatch(paths, body) {
  const clubOrderedPaths = orderPathsForClub(paths, getActiveClubCode());
  return tryRequests(clubOrderedPaths, (p) => apiPatch(p, body));
}
async function tryPut(paths, body) {
  const clubOrderedPaths = orderPathsForClub(paths, getActiveClubCode());
  return tryRequests(clubOrderedPaths, (p) => apiPut(p, body));
}
async function tryDelete(paths) {
  const clubOrderedPaths = orderPathsForClub(paths, getActiveClubCode());
  return tryRequests(clubOrderedPaths, (p) => apiDelete(p));
}

async function tryGetExact(paths, params) {
  return tryRequests(paths, (p) => apiGet(addQuery(p, params)));
}
async function tryPostExact(paths, body) {
  return tryRequests(paths, (p) => apiPost(p, body));
}
async function tryDeleteExact(paths) {
  return tryRequests(paths, (p) => apiDelete(p));
}

/* ---------- helper to generate standard CRUD + club-scoped endpoints ---------- */
function mkSimpleEntity(bases = { admin: null, public: null, alt: [] }) {
  const entityName = bases?.name || bases?.entityName || "Unknown";
  const admin = bases.admin || null;
  const pub = bases.public || null;
  const alt = bases.alt || [];
  const altPaths = normalizePaths(alt);
  const uniq = (arr) => [...new Set(normalizePaths(arr))];
  const ensurePaths = (paths) => {
    const normalized = normalizePaths(paths);
    if (!normalized.length) {
      throw new Error(`No endpoints configured for entity ${entityName}`);
    }
    return normalized;
  };

  const listPaths   = uniq([admin, ...altPaths, pub]);
  const idPaths     = (id) => uniq([admin && `${admin}/${id}`, ...altPaths.map(p => `${p}/${id}`), pub && `${pub}/${id}`]);

  const clubize = clubizePath;
  const listClubPaths = (cr) => uniq([clubize(admin, cr), ...altPaths.map(p => clubize(p, cr)), clubize(pub, cr)]);
  const idClubPaths   = (cr, id) => uniq([clubize(admin, cr) && `${clubize(admin, cr)}/${id}`, ...altPaths.map(p => clubize(p, cr) && `${clubize(p, cr)}/${id}`), clubize(pub, cr) && `${clubize(pub, cr)}/${id}`]);

  return {
    async list(params = {}) {
      const data = await tryGet(ensurePaths(listPaths), params);
      return normalizeEntityList(data, entityName);
    },
    async filter(params = {}) {
      const data = await tryGet(ensurePaths(listPaths), params);
      return normalizeEntityList(data, entityName);
    },
    async get(id)           { return await tryGet(ensurePaths(idPaths(encodeURIComponent(String(id))))); },
    async create(payload)   { return await tryPost(ensurePaths(listPaths), payload); },
    async update(id, body)  { return await tryPatch(ensurePaths(idPaths(encodeURIComponent(String(id)))), body); },
    async delete(id)        { return await tryDelete(ensurePaths(idPaths(encodeURIComponent(String(id))))); },

    async listForClub(clubRef, params = {}) {
      const cr = encodeURIComponent(String(clubRef));
      const data = await tryGet(ensurePaths(listClubPaths(cr)), params);
      return normalizeEntityList(data, entityName);
    },
    async getForClub(clubRef, id)           { const cr = encodeURIComponent(String(clubRef)); const iid = encodeURIComponent(String(id)); return await tryGet(ensurePaths(idClubPaths(cr, iid))); },
    async createForClub(clubRef, payload)   { const cr = encodeURIComponent(String(clubRef)); return await tryPost(ensurePaths(listClubPaths(cr)), payload); },
    async updateForClub(clubRef, id, body)  { const cr = encodeURIComponent(String(clubRef)); const iid = encodeURIComponent(String(id)); return await tryPatch(ensurePaths(idClubPaths(cr, iid)), body); },
    async deleteForClub(clubRef, id)        { const cr = encodeURIComponent(String(clubRef)); const iid = encodeURIComponent(String(id)); return await tryDelete(ensurePaths(idClubPaths(cr, iid))); },
  };
}

/* --------------------- normalizers ----------------------- */
function normalizeUser(u) {
  if (!u) return { id: null, full_name: "", email: "" };
  const first = u.first_name || u.given_name || "";
  const last  = u.last_name || u.family_name || "";
  const fullFromParts = (first + " " + last).trim();
  const full_name = u.full_name ? u.full_name : (fullFromParts ? fullFromParts : (u.name ? u.name : ""));
  let hobbies = [];
  if (Array.isArray(u.hobbies)) hobbies = u.hobbies;
  else if (u.hobbies || u.interests) { const raw = String(u.hobbies || u.interests); hobbies = raw.split(",").map(s => s.trim()).filter(Boolean); }
  return {
    id: pickFirst(u.id, u.user_id, u.sub, u.uuid),
    full_name,
    email: u.email || u.user_email || "",
    birthday: pickFirst(u.birthday, u.date_of_birth),
    date_of_birth: pickFirst(u.date_of_birth, u.birthday),
    hobbies,
    interests: u.interests ?? null,
    onboarded: !!(u.onboarded || u.profile_complete),
    ...u,
  };
}

/* ============================ USER ============================ */
export const User = {
  async getProfile() {
    const auth0User = getCurrentUser();
    let apiData = null;
    let fallbackData = null;
    let fetchStatus = null;

    try {
      apiData = await fetchCurrentUser();
    } catch (error) {
      fetchStatus = error?.status ?? error?.response?.status ?? null;
      if (error?.response?.data) {
        fallbackData = error.response.data;
      } else if (fetchStatus !== 401 && fetchStatus !== 403 && auth0User && !fallbackData) {
        fallbackData = auth0User;
      }
    }

    const merged = apiData
      ? auth0User
        ? { ...auth0User, ...apiData }
        : apiData
      : auth0User
      ? auth0User
      : fallbackData;

    if (!merged && (fetchStatus === 401 || fetchStatus === 403)) {
      return null;
    }

    return normalizeUser(merged);
  },
  async list(params = {}) { return unwrap(await apiGet(addQuery("/api/admin/users", params))); },
  async filter(params = {}) { return this.list(params); },
  async create(payload) { return unwrap(await apiPost("/api/admin/users", payload)); },
  async update(id, payload) { return unwrap(await apiPatch(`/api/admin/users/${encodeURIComponent(String(id))}`, payload)); },
  async delete(id) { return unwrap(await apiDelete(`/api/admin/users/${encodeURIComponent(String(id))}`)); },
  async updateMyUserData(payload) {
    const endpoint = isPlatformContext() ? "/api/admin/me" : "/api/me";
    return unwrap(await apiPatch(endpoint, payload));
  },
};

/* ========================== AUDIT LOG ========================= */
export const AuditLog = {
  async create(payload) { return unwrap(await apiPost("/api/assist/audit", payload)); },
  async list(params = {}) { return unwrap(await apiGet(addQuery("/api/assist/audit", params))); },
  async forUser(userId, params = {}) { return unwrap(await apiGet(addQuery(`/api/assist/audit/user/${encodeURIComponent(String(userId))}`, params))); },
  async get(id) { return unwrap(await apiGet(`/api/assist/audit/${encodeURIComponent(String(id))}`)); },
};

async function memberMutation(method, path, payload) {
  const response = await method(path, payload);
  const envelope = response?.data || response;
  if (response?.ok === false || envelope?.ok === false) throw new Error(envelope?.error || response?.error || 'member_create_failed');
  return envelope?.data || envelope;
}

/* =========================== MEMBER =========================== */
export const Member = {
  async list(params = {}) { return unwrap(await apiGet(addQuery("/api/admin/members", params))); },
  async search(params = {}, clubCode = getActiveClubCode()) {
    if (!clubCode) return { ok: false, error: "missing_club_code" };
    const path = `/api/clubs/${encodeURIComponent(String(clubCode))}/members/search`;
    return unwrap(await apiGet(addQuery(path, params)));
  },
  async listHorses(memberId, params = {}, clubCode = getActiveClubCode()) {
    if (!clubCode) return { ok: false, error: "missing_club_code" };
    const path = `/api/clubs/${encodeURIComponent(String(clubCode))}/members/${encodeURIComponent(
      String(memberId)
    )}/horses`;
    return unwrap(await apiGet(addQuery(path, params)));
  },
  async get(id) { return unwrap(await apiGet(`/api/admin/members/${encodeURIComponent(String(id))}`)); },
  async create(payload) { return memberMutation(apiPost, addQuery('/api/club/members', {club_code:getActiveClubCode()}), payload); },
  async bulkCreate(rows, clubCode = getActiveClubCode()) { return memberMutation(apiPost, addQuery('/api/club/members/import', {club_code:clubCode}), {rows}); },
  async update(id, payload) { return memberMutation(apiPatch, addQuery(`/api/club/members/${encodeURIComponent(String(id))}`, {club_code:getActiveClubCode()}), payload); },
  async delete(id) { return unwrap(await apiDelete(`/api/admin/members/${encodeURIComponent(String(id))}`)); },
  async listForClub(clubRef, params = {}) { return unwrap(await apiGet(addQuery(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/members`, params))); },
  async getForClub(clubRef, id) { return unwrap(await apiGet(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/members/${encodeURIComponent(String(id))}`)); },
  async createForClub(clubRef, payload) { return memberMutation(apiPost, addQuery('/api/club/members', {club_code:clubRef}), payload); },
  async updateForClub(clubRef, id, payload) { return memberMutation(apiPatch, addQuery(`/api/club/members/${encodeURIComponent(String(id))}`, {club_code:clubRef}), payload); },
  async deleteForClub(clubRef, id) { return unwrap(await apiDelete(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/members/${encodeURIComponent(String(id))}`)); },
  async findByEmail(email) { const list = (await this.list({ email })) || []; return Array.isArray(list) ? list[0] || null : null; },
  async findByName(q) { return await this.list({ q }); },
};

/* ============================ EVENT =========================== */
export const Event = {
  async list(params = {}) { return unwrap(await apiGet(addQuery("/api/admin/events", params))); },
  async get(id) { return unwrap(await apiGet(`/api/admin/events/${encodeURIComponent(String(id))}`)); },
  async create(payload) { return unwrap(await apiPost("/api/admin/events", payload)); },
  async update(id, payload) { return unwrap(await apiPatch(`/api/admin/events/${encodeURIComponent(String(id))}`, payload)); },
  async delete(id) { return unwrap(await apiDelete(`/api/admin/events/${encodeURIComponent(String(id))}`)); },
  async listForClub(clubRef, params = {}) { return unwrap(await apiGet(addQuery(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/events`, params))); },
  async getForClub(clubRef, id) { return unwrap(await apiGet(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/events/${encodeURIComponent(String(id))}`)); },
  async createForClub(clubRef, payload) { return unwrap(await apiPost(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/events`, payload)); },
  async updateForClub(clubRef, id, payload) { return unwrap(await apiPatch(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/events/${encodeURIComponent(String(id))}`, payload)); },
  async deleteForClub(clubRef, id) { return unwrap(await apiDelete(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/events/${encodeURIComponent(String(id))}`)); },
};

/* ======================= TEE TIME BOOKING ====================== */
export const TeeTimeBooking = {
  async availability(clubRef, params = {}) { return unwrap(await apiGet(addQuery(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/tee-times/availability`, params))); },
  async listForClub(clubRef, params = {}) { return unwrap(await apiGet(addQuery(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/tee-times/bookings`, params))); },
  async getForClub(clubRef, bookingId) { return unwrap(await apiGet(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/tee-times/bookings/${encodeURIComponent(String(bookingId))}`)); },
  async book(clubRef, payload) { return unwrap(await apiPost(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/tee-times/bookings`, payload)); },
  async update(clubRef, bookingId, payload) { return unwrap(await apiPatch(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/tee-times/bookings/${encodeURIComponent(String(bookingId))}`, payload)); },
  async cancel(clubRef, bookingId) { return unwrap(await apiDelete(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/tee-times/bookings/${encodeURIComponent(String(bookingId))}`)); },
};

/* ============================= INVOICE ========================= */
export const Invoice = {
  async list(params = {}) { return unwrap(await apiGet(addQuery("/api/admin/invoices", params))); },
  async get(id) { return unwrap(await apiGet(`/api/admin/invoices/${encodeURIComponent(String(id))}`)); },
  async create(payload) { return unwrap(await apiPost("/api/admin/invoices", payload)); },
  async update(id, payload) { return unwrap(await apiPatch(`/api/admin/invoices/${encodeURIComponent(String(id))}`, payload)); },
  async delete(id) { return unwrap(await apiDelete(`/api/admin/invoices/${encodeURIComponent(String(id))}`)); },
  async listForClub(clubRef, params = {}) { return unwrap(await apiGet(addQuery(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/invoices`, params))); },
  async getForClub(clubRef, id) { return unwrap(await apiGet(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/invoices/${encodeURIComponent(String(id))}`)); },
  async createForClub(clubRef, payload) { return unwrap(await apiPost(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/invoices`, payload)); },
  async updateForClub(clubRef, id, payload) { return unwrap(await apiPatch(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/invoices/${encodeURIComponent(String(id))}`, payload)); },
  async deleteForClub(clubRef, id) { return unwrap(await apiDelete(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/invoices/${encodeURIComponent(String(id))}`)); },
  async findByMember(memberId, params = {}) { return unwrap(await apiGet(addQuery("/api/admin/invoices", { ...params, member_id: memberId }))); },
  async findByStatus(status, params = {}) { return unwrap(await apiGet(addQuery("/api/admin/invoices", { ...params, status }))); },
};

/* ============================ GOLF COURSE ====================== */
export const GolfCourse = {
  async list(params = {}) { return unwrap(await apiGet(addQuery("/api/admin/golf/courses", params))); },
  async get(id) { return unwrap(await apiGet(`/api/admin/golf/courses/${encodeURIComponent(String(id))}`)); },
  async create(payload) { return unwrap(await apiPost("/api/admin/golf/courses", payload)); },
  async update(id, payload) { return unwrap(await apiPatch(`/api/admin/golf/courses/${encodeURIComponent(String(id))}`, payload)); },
  async delete(id) { return unwrap(await apiDelete(`/api/admin/golf/courses/${encodeURIComponent(String(id))}`)); },
  async listForClub(clubRef, params = {}) { return unwrap(await apiGet(addQuery(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/golf/courses`, params))); },
  async getForClub(clubRef, id) { return unwrap(await apiGet(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/golf/courses/${encodeURIComponent(String(id))}`)); },
  async createForClub(clubRef, payload) { return unwrap(await apiPost(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/golf/courses`, payload)); },
  async updateForClub(clubRef, id, payload) { return unwrap(await apiPatch(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/golf/courses/${encodeURIComponent(String(id))}`, payload)); },
  async deleteForClub(clubRef, id) { return unwrap(await apiDelete(`/api/admin/clubs/${encodeURIComponent(String(clubRef))}/golf/courses/${encodeURIComponent(String(id))}`)); },
};

/* ============================== AI CHAT ======================== */
export const AIChat = {
  async list(params = {}) { return await tryGet(["/api/admin/ai-chat"], params); },
  async get(id) { return await tryGet([`/api/admin/ai-chat/${encodeURIComponent(String(id))}`]); },
  async latest(params = {}) { return await tryGet(["/api/admin/ai-chat/latest"], params); },
  async create(payload) { return await tryPost(["/api/admin/ai-chat"], payload); },
  async update(id, payload) { return await tryPatch([`/api/admin/ai-chat/${encodeURIComponent(String(id))}`], payload); },
  async delete(id) { return await tryDelete([`/api/admin/ai-chat/${encodeURIComponent(String(id))}`]); },
  async history(id, params = {}) {
    return await tryGet([`/api/admin/ai-chat/${encodeURIComponent(String(id))}/messages`], params);
  },
  async appendMessage(id, message) {
    const body = typeof message === "string" ? { content: message } : message;
    return await tryPost([`/api/admin/ai-chat/${encodeURIComponent(String(id))}/messages`], body);
  },
};

// AI Insights (used by AIInsightPanel)
export const AIInsight = mkSimpleEntity({
  admin: "/api/admin/ai/insights",
  public: "/api/ai/insights",
  // optional alternates if your backend uses a different slug:
  alt: [
    "/api/admin/ai-insights",
    "/api/ai-insights",
    "/api/admin/reporting/ai-insights",
    "/api/reporting/ai-insights",
  ],
});

// Engagement scoring (Reporting)
export const EngagementScore = mkSimpleEntity({
  admin: "/api/admin/reporting/engagement-scores",
  public: "/api/reporting/engagement-scores",
  // alternates in case your backend uses a different slug
  alt: [
    "/api/admin/engagement-scores",
    "/api/engagement-scores",
    "/api/admin/analytics/engagement",
    "/api/analytics/engagement"
  ],
});

/* ============================ USER PROFILE ===================== */
export const UserProfile = {
  async getProfile() {
    if (shouldSkipPlatformProfile()) {
      return await User.getProfile();
    }
    try { return await tryGet(["/api/admin/profile", "/api/admin/me", "/api/me"]); }
    catch { return await User.getProfile(); }
  },
  async get(id) {
    if (!id) return await this.getProfile();
    return await tryGet([
      `/api/admin/users/${encodeURIComponent(String(id))}/profile`,
      `/api/admin/user-profiles/${encodeURIComponent(String(id))}`,
      `/api/admin/users/${encodeURIComponent(String(id))}`,
      `/api/admin/profile`,
    ]);
  },
  async update(id, payload) {
    return await tryPatch([
      `/api/admin/users/${encodeURIComponent(String(id))}/profile`,
      `/api/admin/user-profiles/${encodeURIComponent(String(id))}`,
      `/api/admin/users/${encodeURIComponent(String(id))}`,
    ], payload);
  },
  async updateMe(payload) { return await tryPatch([ "/api/admin/me" ], payload); },
};

/* ============================== CLUB USER ====================== */
export const ClubUser = {
  async listForClub(clubRef, params = {}) {
    if (!clubRef) {
      throw new Error("Club reference is required to list club users.");
    }
    const response = await tryGetExact(buildClubUserListPaths(clubRef), params);
    if (!response) {
      throw new Error("Unable to load club users.");
    }
    if (response.ok === false) {
      const error = new Error(response.error || "Unable to load club users.");
      error.status = response.status;
      throw error;
    }
    return response;
  },
  async addToClub(clubRef, payload) {
    if (!clubRef) {
      throw new Error("Club reference is required to add club users.");
    }
    const response = await tryPostExact(buildClubUserListPaths(clubRef), payload);
    if (!response) {
      throw new Error("Unable to add club user.");
    }
    if (response.ok === false) {
      const error = new Error(response.error || "Unable to add club user.");
      error.status = response.status;
      throw error;
    }
    return response;
  },
  async removeFromClub(clubRef, userId) {
    if (!clubRef || !userId) {
      throw new Error("Club reference and user id are required to remove club users.");
    }
    const response = await tryDeleteExact(buildClubUserUserPaths(clubRef, userId));
    if (!response) {
      throw new Error("Unable to remove club user.");
    }
    if (response.ok === false) {
      const error = new Error(response.error || "Unable to remove club user.");
      error.status = response.status;
      throw error;
    }
    return response;
  },
  async list(params = {}) {
    const clubRef = pickFirst(
      getActiveClubCode(),
      params?.club_code,
      params?.clubCode,
      params?.club_id,
      params?.clubId
    );
    if (!clubRef) {
      throw new Error("Club reference is required to list club users.");
    }
    const response = await tryGetExact(buildClubUserListPaths(clubRef), params);
    if (!response) {
      throw new Error("Unable to load club users.");
    }
    if (response.ok === false) {
      const error = new Error(response.error || "Unable to load club users.");
      error.status = response.status;
      throw error;
    }
    return response;
  },
  async filter(params = {}) { return await this.list(params); },
  async getModuleAccess(clubRef, staffId) {
    if (!clubRef || !staffId) return { ok: false, modules: [] };
    const clubCode = encodeURIComponent(String(clubRef));
    const staffKey = encodeURIComponent(String(staffId));
    return await tryGet([
      `/api/admin/clubs/${clubCode}/users/${staffKey}/module-access`,
    ]);
  },
  async updateModuleAccess(clubRef, staffId, payload) {
    if (!clubRef || !staffId) return { ok: false };
    const clubCode = encodeURIComponent(String(clubRef));
    const staffKey = encodeURIComponent(String(staffId));
    return await tryPatch([
      `/api/admin/clubs/${clubCode}/users/${staffKey}/module-access`,
    ], payload);
  },
};

/* ============================= PRIVANA STAFF =================== */
export const PrivanaStaff = {
  async list(params = {}) {
    return await tryGet(["/api/admin/privana-staff"], params);
  },
  async listManagementModules(params = {}) {
    return await tryGet(["/api/admin/management-modules"], params);
  },
  async get(id) {
    if (!id) return null;
    return await tryGet([
      `/api/admin/privana-staff/${encodeURIComponent(String(id))}`,
    ]);
  },
  async getModuleAccess(id) {
    if (!id) return [];
    return await tryGet([
      `/api/admin/privana-staff/${encodeURIComponent(String(id))}/module-access`,
    ]);
  },
  async create(payload) {
    return await tryPost([`/api/admin/privana-staff`], payload);
  },
  async updateModuleAccess(id, payload) {
    if (!id) return null;
    return await tryPatch([
      `/api/admin/privana-staff/${encodeURIComponent(String(id))}/module-access`,
    ], payload);
  },
  async delete(id) {
    if (!id) return null;
    return await tryDelete([
      `/api/admin/privana-staff/${encodeURIComponent(String(id))}`,
    ]);
  },
};

/* ================================ MESSAGE ====================== */
export const Message = {
  async list(params = {}) { return await tryGet([ "/api/admin/messages", "/api/messages" ], params); },
  async get(id) { return await tryGet([ `/api/admin/messages/${encodeURIComponent(String(id))}`, `/api/messages/${encodeURIComponent(String(id))}` ]); },
  async create(payload) { return await tryPost([ "/api/admin/messages", "/api/messages" ], payload); },
  async update(id, payload) { return await tryPatch([ `/api/admin/messages/${encodeURIComponent(String(id))}`, `/api/messages/${encodeURIComponent(String(id))}` ], payload); },
  async delete(id) { return await tryDelete([ `/api/admin/messages/${encodeURIComponent(String(id))}`, `/api/messages/${encodeURIComponent(String(id))}` ]); },
  async listForUser(userId, params = {}) {
    return await tryGet([ `/api/admin/users/${encodeURIComponent(String(userId))}/messages`,
                          `/api/users/${encodeURIComponent(String(userId))}/messages` ], params);
  },
  async markRead(id) {
    const body = { read: true };
    return await tryPatch(
      [ `/api/admin/messages/${encodeURIComponent(String(id))}`,
        `/api/messages/${encodeURIComponent(String(id))}`,
        `/api/admin/messages/${encodeURIComponent(String(id))}/read`,
        `/api/messages/${encodeURIComponent(String(id))}/read` ],
      body
    );
  },
};

/* ============================= SENTIMENT LOG =================== */
export const SentimentLog = {
  async list(params = {}) { return await tryGet([ "/api/admin/sentiment/logs", "/api/admin/sentiment", "/api/sentiment/logs" ], params); },
  async get(id) { return await tryGet([ `/api/admin/sentiment/logs/${encodeURIComponent(String(id))}`, `/api/admin/sentiment/${encodeURIComponent(String(id))}`, `/api/sentiment/logs/${encodeURIComponent(String(id))}` ]); },
  async latest(params = {}) { return await tryGet([ "/api/admin/sentiment/latest", "/api/sentiment/latest" ], params); },
  async create(payload) { return await tryPost([ "/api/admin/sentiment/logs", "/api/admin/sentiment", "/api/sentiment/logs" ], payload); },
  async update(id, payload) { return await tryPatch([ `/api/admin/sentiment/logs/${encodeURIComponent(String(id))}`, `/api/admin/sentiment/${encodeURIComponent(String(id))}`, `/api/sentiment/logs/${encodeURIComponent(String(id))}` ], payload); },
  async delete(id) { return await tryDelete([ `/api/admin/sentiment/logs/${encodeURIComponent(String(id))}`, `/api/admin/sentiment/${encodeURIComponent(String(id))}`, `/api/sentiment/logs/${encodeURIComponent(String(id))}` ]); },
};

/* ============================= REVENUE ALERT =================== */
export const RevenueAlert = {
  async list(params = {}) { return await tryGet([ "/api/admin/revenue/alerts", "/api/admin/revenue-alerts", "/api/revenue/alerts" ], params); },
  async get(id) { return await tryGet([ `/api/admin/revenue/alerts/${encodeURIComponent(String(id))}`, `/api/admin/revenue-alerts/${encodeURIComponent(String(id))}`, `/api/revenue/alerts/${encodeURIComponent(String(id))}` ]); },
  async latest(params = {}) { return await tryGet([ "/api/admin/revenue/alerts/latest", "/api/admin/revenue-alerts/latest", "/api/revenue/alerts/latest" ], params); },
  async create(payload) { return await tryPost([ "/api/admin/revenue/alerts", "/api/admin/revenue-alerts", "/api/revenue/alerts" ], payload); },
  async update(id, payload) { return await tryPatch([ `/api/admin/revenue/alerts/${encodeURIComponent(String(id))}`, `/api/admin/revenue-alerts/${encodeURIComponent(String(id))}`, `/api/revenue/alerts/${encodeURIComponent(String(id))}` ], payload); },
  async delete(id) { return await tryDelete([ `/api/admin/revenue/alerts/${encodeURIComponent(String(id))}`, `/api/admin/revenue-alerts/${encodeURIComponent(String(id))}`, `/api/revenue/alerts/${encodeURIComponent(String(id))}` ]); },

  // Club-scoped variants
  async listForClub(clubRef, params = {}) {
    return await tryGet(
      [ `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/revenue/alerts`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/revenue-alerts` ],
      params
    );
  },
  async getForClub(clubRef, id) {
    return await tryGet(
      [ `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/revenue/alerts/${encodeURIComponent(String(id))}`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/revenue-alerts/${encodeURIComponent(String(id))}` ]
    );
  },
  async createForClub(clubRef, payload) {
    return await tryPost(
      [ `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/revenue/alerts`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/revenue-alerts` ],
      payload
    );
  },
  async updateForClub(clubRef, id, payload) {
    return await tryPatch(
      [ `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/revenue/alerts/${encodeURIComponent(String(id))}`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/revenue-alerts/${encodeURIComponent(String(id))}` ],
      payload
    );
  },
  async deleteForClub(clubRef, id) {
    return await tryDelete(
      [ `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/revenue/alerts/${encodeURIComponent(String(id))}`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/revenue-alerts/${encodeURIComponent(String(id))}` ]
    );
  },
};

/* ================================ STAFF ======================== */
export const Staff = {
  async list(params = {}) { return await tryGet([ "/api/admin/staff", "/api/admin/employees" ], params); },
  async get(id) { return await tryGet([ `/api/admin/staff/${encodeURIComponent(String(id))}`, `/api/admin/employees/${encodeURIComponent(String(id))}` ]); },
  async create(payload) { return await tryPost([ "/api/admin/staff", "/api/admin/employees" ], payload); },
  async update(id, payload) { return await tryPatch([ `/api/admin/staff/${encodeURIComponent(String(id))}`, `/api/admin/employees/${encodeURIComponent(String(id))}` ], payload); },
  async delete(id) { return await tryDelete([ `/api/admin/staff/${encodeURIComponent(String(id))}`, `/api/admin/employees/${encodeURIComponent(String(id))}` ]); },
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/clubs/${cr}/staff`,        // club users
        `/api/admin/clubs/${cr}/users`,  // developer admins
      ],
      params
    );
  },
  async getForClub(clubRef, id) {
    return await tryGet(
      [ `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/staff/${encodeURIComponent(String(id))}`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/employees/${encodeURIComponent(String(id))}` ]
    );
  },
  async createForClub(clubRef, payload) {
    return await tryPost(
      [ `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/staff`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/employees` ],
      payload
    );
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const sid = encodeURIComponent(String(id));
    return await tryPatch(
      [ `/api/clubs/${cr}/staff/${sid}`,               // club-side endpoint
        `/api/admin/clubs/${cr}/staff/${sid}`,         // legacy/admin
        `/api/admin/clubs/${cr}/employees/${sid}` ],
      payload
    );
  },
  async deleteForClub(clubRef, id) {
    return await tryDelete(
      [ `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/staff/${encodeURIComponent(String(id))}`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/employees/${encodeURIComponent(String(id))}` ]
    );
  },
  async birthdaysBetween(startISO, endISO, params = {}) {
    const baseParams = { ...params, start: startISO, end: endISO };
    return await tryGet(
      [ "/api/admin/staff/birthdays", "/api/admin/employees/birthdays" ],
      baseParams
    );
  },
  async birthdaysForClub(clubRef, startISO, endISO, params = {}) {
    const baseParams = { ...params, start: startISO, end: endISO };
    return await tryGet(
      [ `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/staff/birthdays`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/employees/birthdays` ],
      baseParams
    );
  },
};

/* =========================== BIRTHDAY REMINDER ================= */
export const BirthdayReminder = {
  async list(params = {}) { return await tryGet([ "/api/admin/birthday-reminders", "/api/admin/reminders/birthdays" ], params); },
  async get(id) { return await tryGet([ `/api/admin/birthday-reminders/${encodeURIComponent(String(id))}`, `/api/admin/reminders/birthdays/${encodeURIComponent(String(id))}` ]); },
  async create(payload) { return await tryPost([ "/api/admin/birthday-reminders", "/api/admin/reminders/birthdays" ], payload); },
  async update(id, payload) { return await tryPatch([ `/api/admin/birthday-reminders/${encodeURIComponent(String(id))}`, `/api/admin/reminders/birthdays/${encodeURIComponent(String(id))}` ], payload); },
  async delete(id) { return await tryDelete([ `/api/admin/birthday-reminders/${encodeURIComponent(String(id))}`, `/api/admin/reminders/birthdays/${encodeURIComponent(String(id))}` ]); },
  async markDone(id) { return await tryPatch([ `/api/admin/birthday-reminders/${encodeURIComponent(String(id))}`, `/api/admin/reminders/birthdays/${encodeURIComponent(String(id))}` ], { status: "done" }); },
  async snooze(id, days = 7) { return await tryPatch([ `/api/admin/birthday-reminders/${encodeURIComponent(String(id))}`, `/api/admin/reminders/birthdays/${encodeURIComponent(String(id))}` ], { action: "snooze", days }); },
};

/* ========================= CLUB FACILITY CONFIG ================= */
export const ClubFacilityConfig = {
  // Global (optional)
  async list(params = {}) { return await tryGet([ "/api/admin/facility-configs", "/api/admin/facilities/configs" ], params); },
  async get(id) { return await tryGet([ `/api/admin/facility-configs/${encodeURIComponent(String(id))}`, `/api/admin/facilities/configs/${encodeURIComponent(String(id))}` ]); },
  async create(payload) { return await tryPost([ "/api/admin/facility-configs", "/api/admin/facilities/configs" ], payload); },
  async update(id, payload) { return await tryPatch([ `/api/admin/facility-configs/${encodeURIComponent(String(id))}`, `/api/admin/facilities/configs/${encodeURIComponent(String(id))}` ], payload); },
  async delete(id) { return await tryDelete([ `/api/admin/facility-configs/${encodeURIComponent(String(id))}`, `/api/admin/facilities/configs/${encodeURIComponent(String(id))}` ]); },

  // Club-scoped (primary usage)
  async getForClub(clubRef, params = {}) {
    return await tryGet(
      [ `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/facility-config`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/facility/config`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/facilities/config` ],
      params
    );
  },
  async updateForClub(clubRef, payload) {
    return await tryPatch(
      [ `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/facility-config`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/facility/config`,
        `/api/admin/clubs/${encodeURIComponent(String(clubRef))}/facilities/config` ],
      payload
    );
  },
};

/* ================================= BOOKING ================================ */
export const Booking = {
  // Global
  async list(params = {}) {
    return await tryGet(
      [
        "/api/admin/bookings",
        "/api/admin/tee-times/bookings",
        "/api/bookings",
      ],
      params
    );
  },
  async get(id) {
    const bid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/admin/bookings/${bid}`,
        `/api/admin/tee-times/bookings/${bid}`,
        `/api/bookings/${bid}`,
      ]
    );
  },
  async create(payload) {
    return await tryPost(
      [ "/api/admin/bookings", "/api/admin/tee-times/bookings", "/api/bookings" ],
      payload
    );
  },
  async update(id, payload) {
    const bid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/bookings/${bid}`,
        `/api/admin/tee-times/bookings/${bid}`,
        `/api/bookings/${bid}`,
      ],
      payload
    );
  },
  async delete(id) {
    const bid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/admin/bookings/${bid}`,
        `/api/admin/tee-times/bookings/${bid}`,
        `/api/bookings/${bid}`,
      ]
    );
  },

  // Convenience: commonly-used filtered views
  async active(params = {}) { return await this.list({ status: "active", ...params }); },
  async upcoming(params = {}) { return await this.list({ status: "upcoming", ...params }); },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/bookings`,
        `/api/admin/clubs/${cr}/tee-times/bookings`,
        `/api/clubs/${cr}/bookings`,
      ],
      params
    );
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const bid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/bookings/${bid}`,
        `/api/admin/clubs/${cr}/tee-times/bookings/${bid}`,
        `/api/clubs/${cr}/bookings/${bid}`,
      ]
    );
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost(
      [
        `/api/admin/clubs/${cr}/bookings`,
        `/api/admin/clubs/${cr}/tee-times/bookings`,
        `/api/clubs/${cr}/bookings`,
      ],
      payload
    );
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const bid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/clubs/${cr}/bookings/${bid}`,
        `/api/admin/clubs/${cr}/tee-times/bookings/${bid}`,
        `/api/clubs/${cr}/bookings/${bid}`,
      ],
      payload
    );
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const bid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/admin/clubs/${cr}/bookings/${bid}`,
        `/api/admin/clubs/${cr}/tee-times/bookings/${bid}`,
        `/api/clubs/${cr}/bookings/${bid}`,
      ]
    );
  },
};

/* =============================== ROOM SERVICE =============================== */
export const RoomService = {
  // Global
  async list(params = {}) {
    return await tryGet(
      [
        "/api/admin/room-service/orders",
        "/api/admin/room-service",
        "/api/admin/fnb/room-service/orders",
        "/api/room-service/orders",
      ],
      params
    );
  },
  async get(id) {
    const rid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/admin/room-service/orders/${rid}`,
        `/api/admin/room-service/${rid}`,
        `/api/admin/fnb/room-service/orders/${rid}`,
        `/api/room-service/orders/${rid}`,
      ]
    );
  },
  async create(payload) {
    return await tryPost(
      [
        "/api/admin/room-service/orders",
        "/api/admin/room-service",
        "/api/admin/fnb/room-service/orders",
        "/api/room-service/orders",
      ],
      payload
    );
  },
  async update(id, payload) {
    const rid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/room-service/orders/${rid}`,
        `/api/admin/room-service/${rid}`,
        `/api/admin/fnb/room-service/orders/${rid}`,
        `/api/room-service/orders/${rid}`,
      ],
      payload
    );
  },
  async delete(id) {
    const rid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/admin/room-service/orders/${rid}`,
        `/api/admin/room-service/${rid}`,
        `/api/admin/fnb/room-service/orders/${rid}`,
        `/api/room-service/orders/${rid}`,
      ]
    );
  },

  // Status helpers
  async markPrepared(id) {
    const rid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/room-service/orders/${rid}/status`,
        `/api/admin/room-service/${rid}/status`,
        `/api/admin/fnb/room-service/orders/${rid}/status`,
      ],
      { status: "prepared" }
    );
  },
  async markDelivered(id) {
    const rid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/room-service/orders/${rid}/status`,
        `/api/admin/room-service/${rid}/status`,
        `/api/admin/fnb/room-service/orders/${rid}/status`,
      ],
      { status: "delivered" }
    );
  },
  async assignToBooking(id, bookingId) {
    const rid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/room-service/orders/${rid}`,
        `/api/admin/room-service/${rid}`,
        `/api/admin/fnb/room-service/orders/${rid}`,
      ],
      { booking_id: bookingId }
    );
  },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/room-service/orders`,
        `/api/admin/clubs/${cr}/room-service`,
        `/api/admin/clubs/${cr}/fnb/room-service/orders`,
        `/api/clubs/${cr}/room-service/orders`,
      ],
      params
    );
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const rid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/room-service/orders/${rid}`,
        `/api/admin/clubs/${cr}/room-service/${rid}`,
        `/api/admin/clubs/${cr}/fnb/room-service/orders/${rid}`,
        `/api/clubs/${cr}/room-service/orders/${rid}`,
      ]
    );
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost(
      [
        `/api/admin/clubs/${cr}/room-service/orders`,
        `/api/admin/clubs/${cr}/room-service`,
        `/api/admin/clubs/${cr}/fnb/room-service/orders`,
        `/api/clubs/${cr}/room-service/orders`,
      ],
      payload
    );
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const rid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/clubs/${cr}/room-service/orders/${rid}`,
        `/api/admin/clubs/${cr}/room-service/${rid}`,
        `/api/admin/clubs/${cr}/fnb/room-service/orders/${rid}`,
        `/api/clubs/${cr}/room-service/orders/${rid}`,
      ],
      payload
    );
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const rid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/admin/clubs/${cr}/room-service/orders/${rid}`,
        `/api/admin/clubs/${cr}/room-service/${rid}`,
        `/api/admin/clubs/${cr}/fnb/room-service/orders/${rid}`,
        `/api/clubs/${cr}/room-service/orders/${rid}`,
      ]
    );
  },
};

/* ============================== INVENTORY ITEM ============================== */
export const InventoryItem = {
  // Global
  async list(params = {}) {
    return await tryGet(
      [
        "/api/admin/inventory/items",
        "/api/admin/inventory",
        "/api/inventory/items",
      ],
      params
    );
  },
  async get(id) {
    const iid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/admin/inventory/items/${iid}`,
        `/api/admin/inventory/${iid}`,
        `/api/inventory/items/${iid}`,
      ]
    );
  },
  async create(payload) {
    return await tryPost(
      [ "/api/admin/inventory/items", "/api/admin/inventory", "/api/inventory/items" ],
      payload
    );
  },
  async update(id, payload) {
    const iid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/inventory/items/${iid}`,
        `/api/admin/inventory/${iid}`,
        `/api/inventory/items/${iid}`,
      ],
      payload
    );
  },
  async delete(id) {
    const iid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/admin/inventory/items/${iid}`,
        `/api/admin/inventory/${iid}`,
        `/api/inventory/items/${iid}`,
      ]
    );
  },

  // Convenience filters
  async lowStock(params = {}) { return await this.list({ status: "low", ...params }); },
  async byCategory(category, params = {}) { return await this.list({ ...params, category }); },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/inventory/items`,
        `/api/admin/clubs/${cr}/inventory`,
        `/api/clubs/${cr}/inventory/items`,
      ],
      params
    );
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const iid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/inventory/items/${iid}`,
        `/api/admin/clubs/${cr}/inventory/${iid}`,
        `/api/clubs/${cr}/inventory/items/${iid}`,
      ]
    );
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost(
      [
        `/api/admin/clubs/${cr}/inventory/items`,
        `/api/admin/clubs/${cr}/inventory`,
        `/api/clubs/${cr}/inventory/items`,
      ],
      payload
    );
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const iid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/clubs/${cr}/inventory/items/${iid}`,
        `/api/admin/clubs/${cr}/inventory/${iid}`,
        `/api/clubs/${cr}/inventory/items/${iid}`,
      ],
      payload
    );
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const iid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/admin/clubs/${cr}/inventory/items/${iid}`,
        `/api/admin/clubs/${cr}/inventory/${iid}`,
        `/api/clubs/${cr}/inventory/items/${iid}`,
      ]
    );
  },
};

/* ================================ ANNOUNCEMENT =============================== */
export const Announcement = {
  // Global
  async list(params = {}) { return await tryGet([ "/api/admin/announcements", "/api/announcements" ], params); },
  async get(id) {
    const aid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/announcements/${aid}`, `/api/announcements/${aid}` ]);
  },
  async create(payload) { return await tryPost([ "/api/admin/announcements", "/api/announcements" ], payload); },
  async update(id, payload) {
    const aid = encodeURIComponent(String(id));
    return await tryPatch([ `/api/admin/announcements/${aid}`, `/api/announcements/${aid}` ], payload);
  },
  async delete(id) {
    const aid = encodeURIComponent(String(id));
    return await tryDelete([ `/api/admin/announcements/${aid}`, `/api/announcements/${aid}` ]);
  },
  async latest(params = {}) { return await tryGet([ "/api/admin/announcements/latest", "/api/announcements/latest" ], params); },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([ `/api/admin/clubs/${cr}/announcements`, `/api/clubs/${cr}/announcements` ], params);
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const aid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/clubs/${cr}/announcements/${aid}`, `/api/clubs/${cr}/announcements/${aid}` ]);
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost([ `/api/admin/clubs/${cr}/announcements`, `/api/clubs/${cr}/announcements` ], payload);
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const aid = encodeURIComponent(String(id));
    return await tryPatch([ `/api/admin/clubs/${cr}/announcements/${aid}`, `/api/clubs/${cr}/announcements/${aid}` ], payload);
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const aid = encodeURIComponent(String(id));
    return await tryDelete([ `/api/admin/clubs/${cr}/announcements/${aid}`, `/api/clubs/${cr}/announcements/${aid}` ]);
  },
};

/* =============================== WEBSITE CONTENT =============================== */
export const WebsiteContent = {
  // Global
  async list(params = {}) {
    const response = await tryGet(
      [
        "/api/admin/website/content",
        "/api/admin/cms/pages",
        "/api/admin/content/pages",
        "/api/website/content",
      ],
      params
    );
    return normalizeStrictEntityList(response, 'WebsiteContent');
  },
  async get(id) {
    const pid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/admin/website/content/${pid}`,
        `/api/admin/cms/pages/${pid}`,
        `/api/admin/content/pages/${pid}`,
        `/api/website/content/${pid}`,
      ]
    );
  },
  async create(payload) {
    return await tryPost(
      [
        "/api/admin/website/content",
        "/api/admin/cms/pages",
        "/api/admin/content/pages",
      ],
      payload
    );
  },
  async update(id, payload) {
    const pid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/website/content/${pid}`,
        `/api/admin/cms/pages/${pid}`,
        `/api/admin/content/pages/${pid}`,
      ],
      payload
    );
  },
  async delete(id) {
    const pid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/admin/website/content/${pid}`,
        `/api/admin/cms/pages/${pid}`,
        `/api/admin/content/pages/${pid}`,
      ]
    );
  },

  // Convenience
  async bySlug(slug, params = {}) {
    const base = { ...params, slug };
    return await tryGet(
      [
        "/api/admin/website/content/by-slug",
        "/api/admin/cms/pages/by-slug",
        "/api/admin/content/pages/by-slug",
      ],
      base
    );
  },
  async publish(id) {
    const pid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/website/content/${pid}/publish`,
        `/api/admin/cms/pages/${pid}/publish`,
        `/api/admin/content/pages/${pid}/publish`,
      ],
      { published: true }
    );
  },
  async unpublish(id) {
    const pid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/website/content/${pid}/publish`,
        `/api/admin/cms/pages/${pid}/publish`,
        `/api/admin/content/pages/${pid}/publish`,
      ],
      { published: false }
    );
  },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/website/content`,
        `/api/admin/clubs/${cr}/cms/pages`,
        `/api/admin/clubs/${cr}/content/pages`,
        `/api/clubs/${cr}/website/content`,
      ],
      params
    );
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const pid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/website/content/${pid}`,
        `/api/admin/clubs/${cr}/cms/pages/${pid}`,
        `/api/admin/clubs/${cr}/content/pages/${pid}`,
        `/api/clubs/${cr}/website/content/${pid}`,
      ]
    );
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost(
      [
        `/api/admin/clubs/${cr}/website/content`,
        `/api/admin/clubs/${cr}/cms/pages`,
        `/api/admin/clubs/${cr}/content/pages`,
      ],
      payload
    );
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const pid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/clubs/${cr}/website/content/${pid}`,
        `/api/admin/clubs/${cr}/cms/pages/${pid}`,
        `/api/admin/clubs/${cr}/content/pages/${pid}`,
      ],
      payload
    );
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const pid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/admin/clubs/${cr}/website/content/${pid}`,
        `/api/admin/clubs/${cr}/cms/pages/${pid}`,
        `/api/admin/clubs/${cr}/content/pages/${pid}`,
      ]
    );
  },
  async bySlugForClub(clubRef, slug, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    const p = { ...params, slug };
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/website/content/by-slug`,
        `/api/admin/clubs/${cr}/cms/pages/by-slug`,
        `/api/admin/clubs/${cr}/content/pages/by-slug`,
      ],
      p
    );
  },
};

/* =============================== CLUB PROFILE =============================== */
export const ClubProfile = {
  async getForClub(clubRef) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([
      `/api/admin/clubs/${cr}/profile`,
      `/api/clubs/${cr}/profile`,
    ]);
  },
  async updateForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPatch([
      `/api/admin/clubs/${cr}/profile`,
      `/api/clubs/${cr}/profile`,
    ], payload);
  },
  async uploadLogoForClub(clubRef, formData) {
    const cr = encodeURIComponent(String(clubRef));
    const body = formData instanceof FormData ? formData : (() => {
      const fd = new FormData();
      if (formData) fd.append('file', formData);
      return fd;
    })();
    return await tryPost([
      `/api/clubs/${cr}/club-logo`,
    ], body);
  },
};

export const ClubCurrency = {
  async getForClub(clubRef) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([
      `/api/admin/clubs/${cr}/currency`,
      `/api/clubs/${cr}/currency`,
    ]);
  },
  async updateForClub(clubRef, currencyCode) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPut([
      `/api/admin/clubs/${cr}/currency`,
      `/api/clubs/${cr}/currency`,
    ], { currency_code: currencyCode });
  },
};

export const ClubContext = {
  async getForClub(clubRef) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([
      `/api/clubs/${cr}/context`,
      `/api/admin/clubs/${cr}/context`,
    ]);
  },
};

// Public site form submissions (contact forms, etc.)
export const FormSubmission = mkSimpleEntity({
  admin: "/api/admin/forms/submissions",
  public: "/api/forms/submissions",
  // alternates if your backend uses a different slug
  alt: [
    "/api/admin/form-submissions",
    "/api/form-submissions",
    "/api/admin/public/form-submissions",
    "/api/public/form-submissions",
  ],
});

/* ============================== MEMBERSHIP TYPE ============================== */
// Dedicated helper for club-scoped membership endpoints (no admin fallbacks)
export const ClubMembershipApi = {
  async listTypes(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([`/api/clubs/${cr}/membership/types`], params);
  },
  async createType(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost([`/api/clubs/${cr}/membership/types`], payload);
  },
  async updateType(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const mid = encodeURIComponent(String(id));
    return await tryPatch([`/api/clubs/${cr}/membership/types/${mid}`], payload);
  },

  async listFacilityAccess(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([`/api/clubs/${cr}/membership/facility-access`], params);
  },
  async createFacilityAccess(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost([`/api/clubs/${cr}/membership/facility-access`], payload);
  },
  async updateFacilityAccess(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const rid = encodeURIComponent(String(id));
    return await tryPatch([`/api/clubs/${cr}/membership/facility-access/${rid}`], payload);
  },

  async listLocations(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([`/api/clubs/${cr}/locations`], params);
  },

  async listTaxCategories(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([`/api/clubs/${cr}/tax/categories`], params);
  },

  async getPricingPolicies(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([`/api/clubs/${cr}/membership/pricing-policies`], params);
  },

  async upsertPricingPolicies(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPut([`/api/clubs/${cr}/membership/pricing-policies`], payload);
  },
};

export const MembershipType = {
  // Global
  async list(params = {}) {
    return await tryGet(
      [ "/api/admin/membership/types", "/api/admin/memberships/types", "/api/membership/types" ],
      params
    );
  },
  async get(id) {
    const mid = encodeURIComponent(String(id));
    return await tryGet(
      [ `/api/admin/membership/types/${mid}`, `/api/admin/memberships/types/${mid}`, `/api/membership/types/${mid}` ]
    );
  },
  async create(payload) {
    return await tryPost(
      [ "/api/admin/membership/types", "/api/admin/memberships/types", "/api/membership/types" ],
      payload
    );
  },
  async update(id, payload) {
    const mid = encodeURIComponent(String(id));
    return await tryPatch(
      [ `/api/admin/membership/types/${mid}`, `/api/admin/memberships/types/${mid}`, `/api/membership/types/${mid}` ],
      payload
    );
  },
  async delete(id) {
    const mid = encodeURIComponent(String(id));
    return await tryDelete(
      [ `/api/admin/membership/types/${mid}`, `/api/admin/memberships/types/${mid}`, `/api/membership/types/${mid}` ]
    );
  },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/clubs/${cr}/membership/types`,
        `/api/admin/clubs/${cr}/membership/types`,
      ],
      params
    );
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const mid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/clubs/${cr}/membership/types/${mid}`,
        `/api/admin/clubs/${cr}/membership/types/${mid}`,
      ]
    );
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost(
      [
        `/api/clubs/${cr}/membership/types`,
        `/api/admin/clubs/${cr}/membership/types`,
      ],
      payload
    );
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const mid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/clubs/${cr}/membership/types/${mid}`,
        `/api/admin/clubs/${cr}/membership/types/${mid}`,
      ],
      payload
    );
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const mid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/clubs/${cr}/membership/types/${mid}`,
        `/api/admin/clubs/${cr}/membership/types/${mid}`,
      ]
    );
  },
};

/* ============================== RECURRING INVOICE =========================== */
export const RecurringInvoice = {
  // Global
  async list(params = {}) {
    return await tryGet(
      [ "/api/admin/recurring-invoices", "/api/admin/invoices/recurring", "/api/recurring-invoices" ],
      params
    );
  },
  async get(id) {
    const rid = encodeURIComponent(String(id));
    return await tryGet(
      [ `/api/admin/recurring-invoices/${rid}`, `/api/admin/invoices/recurring/${rid}`, `/api/recurring-invoices/${rid}` ]
    );
  },
  async create(payload) {
    return await tryPost(
      [ "/api/admin/recurring-invoices", "/api/admin/invoices/recurring", "/api/recurring-invoices" ],
      payload
    );
  },
  async update(id, payload) {
    const rid = encodeURIComponent(String(id));
    return await tryPatch(
      [ `/api/admin/recurring-invoices/${rid}`, `/api/admin/invoices/recurring/${rid}`, `/api/recurring-invoices/${rid}` ],
      payload
    );
  },
  async delete(id) {
    const rid = encodeURIComponent(String(id));
    return await tryDelete(
      [ `/api/admin/recurring-invoices/${rid}`, `/api/admin/invoices/recurring/${rid}`, `/api/recurring-invoices/${rid}` ]
    );
  },

  // Actions
  async pause(id) {
    const rid = encodeURIComponent(String(id));
    return await tryPatch(
      [ `/api/admin/recurring-invoices/${rid}/status`, `/api/admin/invoices/recurring/${rid}/status` ],
      { status: "paused" }
    );
  },
  async resume(id) {
    const rid = encodeURIComponent(String(id));
    return await tryPatch(
      [ `/api/admin/recurring-invoices/${rid}/status`, `/api/admin/invoices/recurring/${rid}/status` ],
      { status: "active" }
    );
  },
  async runNow(id) {
    const rid = encodeURIComponent(String(id));
    return await tryPost(
      [ `/api/admin/recurring-invoices/${rid}/run`, `/api/admin/invoices/recurring/${rid}/run` ],
      {}
    );
  },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [ `/api/admin/clubs/${cr}/recurring-invoices`, `/api/admin/clubs/${cr}/invoices/recurring`, `/api/clubs/${cr}/recurring-invoices` ],
      params
    );
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const rid = encodeURIComponent(String(id));
    return await tryGet(
      [ `/api/admin/clubs/${cr}/recurring-invoices/${rid}`, `/api/admin/clubs/${cr}/invoices/recurring/${rid}`, `/api/clubs/${cr}/recurring-invoices/${rid}` ]
    );
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost(
      [ `/api/admin/clubs/${cr}/recurring-invoices`, `/api/admin/clubs/${cr}/invoices/recurring`, `/api/clubs/${cr}/recurring-invoices` ],
      payload
    );
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const rid = encodeURIComponent(String(id));
    return await tryPatch(
      [ `/api/admin/clubs/${cr}/recurring-invoices/${rid}`, `/api/admin/clubs/${cr}/invoices/recurring/${rid}`, `/api/clubs/${cr}/recurring-invoices/${rid}` ],
      payload
    );
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const rid = encodeURIComponent(String(id));
    return await tryDelete(
      [ `/api/admin/clubs/${cr}/recurring-invoices/${rid}`, `/api/admin/clubs/${cr}/invoices/recurring/${rid}`, `/api/clubs/${cr}/recurring-invoices/${rid}` ]
    );
  },
};

/* ============================== CLUB TAX PROFILE ============================== */
/** Club-level tax registration/settings used when calculating invoices in MemberForm. */
export const ClubTaxProfile = {
  async getForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/tax/profile`,
        `/api/admin/clubs/${cr}/tax-profile`,
        `/api/clubs/${cr}/tax/profile`,
      ],
      params
    );
  },
  async updateForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPatch(
      [
        `/api/admin/clubs/${cr}/tax/profile`,
        `/api/admin/clubs/${cr}/tax-profile`,
        `/api/clubs/${cr}/tax/profile`,
      ],
      payload
    );
  },

  // Optional globals if your backend supports them
  async list(params = {}) {
    return await tryGet(
      [ `/api/admin/tax/profiles`, `/api/tax/profiles` ],
      params
    );
  },
  async get(id) {
    const tid = encodeURIComponent(String(id));
    return await tryGet(
      [ `/api/admin/tax/profiles/${tid}`, `/api/tax/profiles/${tid}` ]
    );
  },
};

/* -------- Tax categories (global & club-scoped) -------- */
const TaxCategoryBase = mkSimpleEntity({
  admin: "/api/admin/tax/categories",
  public: "/api/tax/categories",
});

export const TaxCategory = {
  ...TaxCategoryBase,
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/clubs/${cr}/tax/categories`,
        `/api/admin/clubs/${cr}/tax/categories`,
      ],
      params
    );
  },
};

/* ================================== TAX RATE ================================= */
export const TaxRate = {
  // Global
  async list(params = {}) {
    return await tryGet(
      [ `/api/admin/tax/rates`, `/api/tax/rates` ],
      params
    );
  },
  async get(id) {
    const rid = encodeURIComponent(String(id));
    return await tryGet(
      [ `/api/admin/tax/rates/${rid}`, `/api/tax/rates/${rid}` ]
    );
  },
  async create(payload) {
    return await tryPost(
      [ `/api/admin/tax/rates`, `/api/tax/rates` ],
      payload
    );
  },
  async update(id, payload) {
    const rid = encodeURIComponent(String(id));
    return await tryPatch(
      [ `/api/admin/tax/rates/${rid}`, `/api/tax/rates/${rid}` ],
      payload
    );
  },
  async delete(id) {
    const rid = encodeURIComponent(String(id));
    return await tryDelete(
      [ `/api/admin/tax/rates/${rid}`, `/api/tax/rates/${rid}` ]
    );
  },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/tax/codes`,
        `/api/clubs/${cr}/tax/codes`,
      ],
      params
    );
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const rid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/tax/codes/${rid}`,
        `/api/clubs/${cr}/tax/codes/${rid}`,
      ]
    );
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost(
      [
        `/api/admin/clubs/${cr}/tax/codes`,
        `/api/clubs/${cr}/tax/codes`,
      ],
      payload
    );
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const rid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/clubs/${cr}/tax/codes/${rid}`,
        `/api/clubs/${cr}/tax/codes/${rid}`,
      ],
      payload
    );
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const rid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/admin/clubs/${cr}/tax/codes/${rid}`,
        `/api/clubs/${cr}/tax/codes/${rid}`,
      ]
    );
  },
};

/* ================================== PAYMENT ================================== */
export const Payment = {
  // Global
  async list(params = {}) {
    return await tryGet(
      [ `/api/admin/payments`, `/api/payments`, `/api/admin/invoices/payments` ],
      params
    );
  },
  async get(id) {
    const pid = encodeURIComponent(String(id));
    return await tryGet(
      [ `/api/admin/payments/${pid}`, `/api/payments/${pid}`, `/api/admin/invoices/payments/${pid}` ]
    );
  },
  async create(payload) {
    return await tryPost(
      [ `/api/admin/payments`, `/api/payments`, `/api/admin/invoices/payments` ],
      payload
    );
  },
  async update(id, payload) {
    const pid = encodeURIComponent(String(id));
    return await tryPatch(
      [ `/api/admin/payments/${pid}`, `/api/payments/${pid}`, `/api/admin/invoices/payments/${pid}` ],
      payload
    );
  },
  async delete(id) {
    const pid = encodeURIComponent(String(id));
    return await tryDelete(
      [ `/api/admin/payments/${pid}`, `/api/payments/${pid}`, `/api/admin/invoices/payments/${pid}` ]
    );
  },

  // Actions
  async refund(id, payload = {}) {
    const pid = encodeURIComponent(String(id));
    return await tryPost(
      [ `/api/admin/payments/${pid}/refund`, `/api/payments/${pid}/refund` ],
      payload
    );
  },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/payments`,
        `/api/admin/clubs/${cr}/invoices/payments`,
        `/api/clubs/${cr}/payments`,
      ],
      params
    );
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const pid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/payments/${pid}`,
        `/api/admin/clubs/${cr}/invoices/payments/${pid}`,
        `/api/clubs/${cr}/payments/${pid}`,
      ]
    );
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost(
      [
        `/api/admin/clubs/${cr}/payments`,
        `/api/admin/clubs/${cr}/invoices/payments`,
        `/api/clubs/${cr}/payments`,
      ],
      payload
    );
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const pid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/clubs/${cr}/payments/${pid}`,
        `/api/admin/clubs/${cr}/invoices/payments/${pid}`,
        `/api/clubs/${cr}/payments/${pid}`,
      ],
      payload
    );
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const pid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/admin/clubs/${cr}/payments/${pid}`,
        `/api/admin/clubs/${cr}/invoices/payments/${pid}`,
        `/api/clubs/${cr}/payments/${pid}`,
      ]
    );
  },
};

/* ============================ MEMBER INTERACTION ============================ */
export const MemberInteraction = {
  // Global
  async list(params = {}) { return await tryGet([ "/api/admin/member-interactions", "/api/admin/interactions", "/api/interactions" ], params); },
  async get(id) {
    const iid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/member-interactions/${iid}`, `/api/admin/interactions/${iid}`, `/api/interactions/${iid}` ]);
  },
  async create(payload) { return await tryPost([ "/api/admin/member-interactions", "/api/admin/interactions", "/api/interactions" ], payload); },
  async update(id, payload) {
    const iid = encodeURIComponent(String(id));
    return await tryPatch([ `/api/admin/member-interactions/${iid}`, `/api/admin/interactions/${iid}`, `/api/interactions/${iid}` ], payload);
  },
  async delete(id) {
    const iid = encodeURIComponent(String(id));
    return await tryDelete([ `/api/admin/member-interactions/${iid}`, `/api/admin/interactions/${iid}`, `/api/interactions/${iid}` ]);
  },

  // By member
  async listForMember(memberId, params = {}) {
    const mid = encodeURIComponent(String(memberId));
    return await tryGet([ `/api/admin/members/${mid}/interactions`, `/api/members/${mid}/interactions` ], params);
  },
  async createForMember(memberId, payload) {
    const mid = encodeURIComponent(String(memberId));
    return await tryPost([ `/api/admin/members/${mid}/interactions`, `/api/members/${mid}/interactions` ], payload);
  },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([ `/api/admin/clubs/${cr}/member-interactions`, `/api/clubs/${cr}/interactions` ], params);
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const iid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/clubs/${cr}/member-interactions/${iid}`, `/api/clubs/${cr}/interactions/${iid}` ]);
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost([ `/api/admin/clubs/${cr}/member-interactions`, `/api/clubs/${cr}/interactions` ], payload);
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const iid = encodeURIComponent(String(id));
    return await tryPatch([ `/api/admin/clubs/${cr}/member-interactions/${iid}`, `/api/clubs/${cr}/interactions/${iid}` ], payload);
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const iid = encodeURIComponent(String(id));
    return await tryDelete([ `/api/admin/clubs/${cr}/member-interactions/${iid}`, `/api/clubs/${cr}/interactions/${iid}` ]);
  },
};

/* ============================ MEMBER ACTIVITY LOG =========================== */
export const MemberActivityLog = {
  // Global
  async list(params = {}) { return await tryGet([ "/api/admin/member-activity/logs", "/api/admin/member-activity", "/api/member-activity/logs" ], params); },
  async get(id) {
    const aid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/member-activity/logs/${aid}`, `/api/admin/member-activity/${aid}`, `/api/member-activity/logs/${aid}` ]);
  },
  async create(payload) { return await tryPost([ "/api/admin/member-activity/logs", "/api/admin/member-activity", "/api/member-activity/logs" ], payload); },

  // By member
  async listForMember(memberId, params = {}) {
    const mid = encodeURIComponent(String(memberId));
    return await tryGet([ `/api/admin/members/${mid}/activity/logs`, `/api/members/${mid}/activity/logs` ], params);
  },
  async createForMember(memberId, payload) {
    const mid = encodeURIComponent(String(memberId));
    return await tryPost([ `/api/admin/members/${mid}/activity/logs`, `/api/members/${mid}/activity/logs` ], payload);
  },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([ `/api/admin/clubs/${cr}/member-activity/logs`, `/api/clubs/${cr}/member-activity/logs` ], params);
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const aid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/clubs/${cr}/member-activity/logs/${aid}`, `/api/clubs/${cr}/member-activity/logs/${aid}` ]);
  },
};

/* ================================== CALL RECORD ================================== */
export const CallRecord = {
  // Global
  async list(params = {}) { return await tryGet([ "/api/admin/calls", "/api/admin/call-records", "/api/calls" ], params); },
  async get(id) {
    const cid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/calls/${cid}`, `/api/admin/call-records/${cid}`, `/api/calls/${cid}` ]);
  },
  async create(payload) { return await tryPost([ "/api/admin/calls", "/api/admin/call-records", "/api/calls" ], payload); },
  async update(id, payload) {
    const cid = encodeURIComponent(String(id));
    return await tryPatch([ `/api/admin/calls/${cid}`, `/api/admin/call-records/${cid}`, `/api/calls/${cid}` ], payload);
  },
  async delete(id) {
    const cid = encodeURIComponent(String(id));
    return await tryDelete([ `/api/admin/calls/${cid}`, `/api/admin/call-records/${cid}`, `/api/calls/${cid}` ]);
  },

  // Attachments / analytics helpers
  async transcript(id) {
    const cid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/calls/${cid}/transcript`, `/api/admin/call-records/${cid}/transcript`, `/api/calls/${cid}/transcript` ]);
  },
  async summary(id) {
    const cid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/calls/${cid}/summary`, `/api/admin/call-records/${cid}/summary`, `/api/calls/${cid}/summary` ]);
  },
  async analytics(id, params = {}) {
    const cid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/calls/${cid}/analytics`, `/api/admin/call-records/${cid}/analytics`, `/api/calls/${cid}/analytics` ], params);
  },

  // Member / Booking relations
  async listForMember(memberId, params = {}) {
    const mid = encodeURIComponent(String(memberId));
    return await tryGet([ `/api/admin/members/${mid}/calls`, `/api/members/${mid}/calls` ], params);
  },
  async listForBooking(bookingId, params = {}) {
    const bid = encodeURIComponent(String(bookingId));
    return await tryGet([ `/api/admin/bookings/${bid}/calls`, `/api/bookings/${bid}/calls` ], params);
  },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([ `/api/admin/clubs/${cr}/calls`, `/api/clubs/${cr}/calls` ], params);
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const cid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/clubs/${cr}/calls/${cid}`, `/api/clubs/${cr}/calls/${cid}` ]);
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost([ `/api/admin/clubs/${cr}/calls`, `/api/clubs/${cr}/calls` ], payload);
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const cid = encodeURIComponent(String(id));
    return await tryPatch([ `/api/admin/clubs/${cr}/calls/${cid}`, `/api/clubs/${cr}/calls/${cid}` ], payload);
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const cid = encodeURIComponent(String(id));
    return await tryDelete([ `/api/admin/clubs/${cr}/calls/${cid}`, `/api/clubs/${cr}/calls/${cid}` ]);
  },
};

/* ============================== ACCOMMODATION UNIT ============================== */
export const AccommodationUnit = {
  // Global
  async list(params = {}) { return await tryGet([ "/api/admin/accommodations/units", "/api/admin/units", "/api/accommodations/units" ], params); },
  async get(id) {
    const uid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/accommodations/units/${uid}`, `/api/admin/units/${uid}`, `/api/accommodations/units/${uid}` ]);
  },
  async create(payload) { return await tryPost([ "/api/admin/accommodations/units", "/api/admin/units", "/api/accommodations/units" ], payload); },
  async update(id, payload) {
    const uid = encodeURIComponent(String(id));
    return await tryPatch([ `/api/admin/accommodations/units/${uid}`, `/api/admin/units/${uid}`, `/api/accommodations/units/${uid}` ], payload);
  },
  async delete(id) {
    const uid = encodeURIComponent(String(id));
    return await tryDelete([ `/api/admin/accommodations/units/${uid}`, `/api/admin/units/${uid}`, `/api/accommodations/units/${uid}` ]);
  },

  // Availability / status helpers
  async availability(params = {}) {
    return await tryGet([ "/api/admin/accommodations/units/availability", "/api/admin/units/availability", "/api/accommodations/units/availability" ], params);
  },
  async setStatus(id, status) {
    const uid = encodeURIComponent(String(id));
    return await tryPatch([ `/api/admin/accommodations/units/${uid}/status`, `/api/admin/units/${uid}/status` ], { status });
  },
  async setHousekeepingStatus(id, status) {
    const uid = encodeURIComponent(String(id));
    return await tryPatch([ `/api/admin/accommodations/units/${uid}/housekeeping`, `/api/admin/units/${uid}/housekeeping` ], { status });
  },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([ `/api/admin/clubs/${cr}/accommodations/units`, `/api/admin/clubs/${cr}/units`, `/api/clubs/${cr}/accommodations/units` ], params);
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const uid = encodeURIComponent(String(id));
    return await tryGet([ `/api/admin/clubs/${cr}/accommodations/units/${uid}`, `/api/admin/clubs/${cr}/units/${uid}`, `/api/clubs/${cr}/accommodations/units/${uid}` ]);
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost([ `/api/admin/clubs/${cr}/accommodations/units`, `/api/admin/clubs/${cr}/units`, `/api/clubs/${cr}/accommodations/units` ], payload);
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const uid = encodeURIComponent(String(id));
    return await tryPatch([ `/api/admin/clubs/${cr}/accommodations/units/${uid}`, `/api/admin/clubs/${cr}/units/${uid}`, `/api/clubs/${cr}/accommodations/units/${uid}` ], payload);
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const uid = encodeURIComponent(String(id));
    return await tryDelete([ `/api/admin/clubs/${cr}/accommodations/units/${uid}`, `/api/admin/clubs/${cr}/units/${uid}`, `/api/clubs/${cr}/accommodations/units/${uid}` ]);
  },
  async availabilityForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet([ `/api/admin/clubs/${cr}/accommodations/units/availability`, `/api/admin/clubs/${cr}/units/availability`, `/api/clubs/${cr}/accommodations/units/availability` ], params);
  },
};

/* ----------------------- DEFINE THE REMAINING ENTITIES ----------------------- */
// Safety / Incidents
export const IncidentReport          = mkSimpleEntity({ admin: "/api/admin/incidents",                    alt: ["/api/admin/incident-reports"],            public: "/api/incidents" });
export const SafetyChecklistTemplate = mkSimpleEntity({ admin: "/api/admin/safety/checklist-templates",  alt: ["/api/admin/checklist-templates"],        public: "/api/safety/checklist-templates" });
export const StaffCertification      = mkSimpleEntity({ admin: "/api/admin/staff/certifications",        public: "/api/staff/certifications" });
export const SafetyIncident          = IncidentReport;
export const Incident                = IncidentReport;
export const Certification           = StaffCertification;
export const ChecklistTemplate       = SafetyChecklistTemplate;

// CRM / Members
export const MemberActivity          = mkSimpleEntity({ admin: "/api/admin/members/activity",            public: "/api/members/activity" });
export const MemberAccount           = mkSimpleEntity({ admin: "/api/admin/accounting/member-accounts",  public: "/api/accounting/member-accounts" });
export const Sequence                = mkSimpleEntity({ admin: "/api/admin/crm/sequences",               public: "/api/crm/sequences" });
export const MessageTemplate         = mkSimpleEntity({ admin: "/api/admin/crm/message-templates",       public: "/api/crm/message-templates" });
const LeadBase           = mkSimpleEntity({ admin: "/api/admin/crm/leads",                   public: "/api/crm/leads" });
export const Lead = {
  ...LeadBase,
  async listForClub(clubCode) {
    const res = await apiGet(`/api/clubs/${encodeURIComponent(clubCode)}/crm/leads`);
    if (!res || res.ok === false) {
      console.error("Lead.listForClub CRM error", res);
      return [];
    }
    const leads = Array.isArray(res.leads) ? res.leads : Array.isArray(res.data) ? res.data : [];
    return leads;
  },
};

const LeadActivityBase   = mkSimpleEntity({ admin: "/api/admin/crm/lead-activity",           public: "/api/crm/lead-activity" });
export const LeadActivity = {
  ...LeadActivityBase,
  async listForClub(clubCode, options = {}) {
    // TODO: replace with real CRM lead activity endpoint when available
    console.warn("LeadActivity.listForClub placeholder called for club", clubCode, options);
    return [];
  },
};
export const CRMWorkflow             = mkSimpleEntity({ admin: "/api/admin/crm/workflows",               public: "/api/crm/workflows" });
export const Task                    = mkSimpleEntity({ admin: "/api/admin/tasks",                       public: "/api/tasks" });

export const DuesPlan = mkSimpleEntity({
  admin: "/api/admin/accounting/dues-plans",
  public: "/api/accounting/dues-plans",
  alt: [
    "/api/admin/dues/plans",
    "/api/dues/plans",
    "/api/admin/membership/dues-plans",
    "/api/membership/dues-plans",
  ],
});

// Accounting / POS
export const POSTransaction          = mkSimpleEntity({ admin: "/api/admin/pos/transactions",            public: "/api/pos/transactions" });
export const FinancialTransaction    = mkSimpleEntity({ admin: "/api/admin/accounting/transactions",     public: "/api/accounting/transactions" });
export const Statement               = mkSimpleEntity({ admin: "/api/admin/accounting/statements",       public: "/api/accounting/statements" });
export const CreditMemo              = mkSimpleEntity({ admin: "/api/admin/accounting/credit-memos",     public: "/api/accounting/credit-memos" });
export const Refund                  = mkSimpleEntity({ admin: "/api/admin/accounting/refunds",          public: "/api/accounting/refunds" });
export const Budget                  = mkSimpleEntity({ admin: "/api/admin/budgeting/budgets",           public: "/api/budgeting/budgets" });
export const FinancialReport         = mkSimpleEntity({ admin: "/api/admin/accounting/reports",          public: "/api/accounting/reports" });

// Procurement / Inventory
export const PurchaseOrder           = mkSimpleEntity({ admin: "/api/admin/procurement/purchase-orders", public: "/api/procurement/purchase-orders" });
export const PurchaseOrderLine       = mkSimpleEntity({ admin: "/api/admin/procurement/purchase-order-lines", public: "/api/procurement/purchase-order-lines" });
export const PurchaseOrderReceipt    = mkSimpleEntity({ admin: "/api/admin/procurement/receipts",        public: "/api/procurement/receipts" });
export const PurchaseOrderReceiptLine= mkSimpleEntity({ admin: "/api/admin/procurement/receipt-lines",   public: "/api/procurement/receipt-lines" });
export const InventoryLog            = mkSimpleEntity({ admin: "/api/admin/inventory/logs",              public: "/api/inventory/logs" });
export const Supplier                = mkSimpleEntity({ admin: "/api/admin/procurement/suppliers",       public: "/api/procurement/suppliers" });
export const RFQ                     = mkSimpleEntity({ admin: "/api/admin/procurement/rfqs",            public: "/api/procurement/rfqs" });
export const IntegrationConnection   = mkSimpleEntity({ admin: "/api/admin/integrations/connections",    public: "/api/integrations/connections" });

// Weddings
export const WeddingOpportunity      = mkSimpleEntity({ admin: "/api/admin/weddings/opportunities",      public: "/api/weddings/opportunities" });
export const WeddingGuest            = mkSimpleEntity({ admin: "/api/admin/weddings/guests",             public: "/api/weddings/guests" });
export const WeddingAuditLog         = mkSimpleEntity({ admin: "/api/admin/weddings/audit-logs",         public: "/api/weddings/audit-logs" });

// Maps
export const MapConfiguration        = mkSimpleEntity({ admin: "/api/admin/map/configurations",          public: "/api/map/configurations" });
export const MapZone                 = mkSimpleEntity({ admin: "/api/admin/map/zones",                   public: "/api/map/zones" });

// Facilities / Sports / Rooms
export const RacquetCourt            = mkSimpleEntity({ admin: "/api/admin/racquet/courts",              public: "/api/racquet/courts" });
export const RoomReservation         = mkSimpleEntity({ admin: "/api/admin/rooms/reservations",          public: "/api/rooms/reservations" });

// IoT / Security
export const MonitoredAsset          = mkSimpleEntity({ admin: "/api/admin/iot/assets",                  public: "/api/iot/assets" });
export const Measurement             = mkSimpleEntity({ admin: "/api/admin/iot/measurements",            public: "/api/iot/measurements" });
export const Camera                  = mkSimpleEntity({ admin: "/api/admin/security/cameras",            public: "/api/security/cameras" });
export const Alert                   = mkSimpleEntity({ admin: "/api/admin/alerts",                      public: "/api/alerts" });

// Misc domain models
export const Golfer                  = mkSimpleEntity({ admin: "/api/admin/golfers",                     public: "/api/golfers" });
export const MaintenanceTicket       = mkSimpleEntity({ admin: "/api/admin/maintenance/tickets",         public: "/api/maintenance/tickets" });
const MembershipFacilityAccessBase = mkSimpleEntity({ admin: "/api/admin/memberships/facility-access", public: "/api/memberships/facility-access" });
export const MembershipFacilityAccess = {
  ...MembershipFacilityAccessBase,
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/clubs/${cr}/membership/facility-access`,
        `/api/admin/clubs/${cr}/membership/facility-access`,
      ],
      params
    );
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost(
      [
        `/api/clubs/${cr}/membership/facility-access`,
        `/api/admin/clubs/${cr}/membership/facility-access`,
      ],
      payload
    );
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const rid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/clubs/${cr}/membership/facility-access/${rid}`,
        `/api/admin/clubs/${cr}/membership/facility-access/${rid}`,
      ],
      payload
    );
  },
};

const LocationBase = mkSimpleEntity({ admin: "/api/admin/locations", public: "/api/locations" });
export const Location = {
  ...LocationBase,
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/clubs/${cr}/locations`,
        `/api/admin/clubs/${cr}/locations`,
      ],
      params
    );
  },
};

// AI / Analytics
export const AIInteractionLog        = mkSimpleEntity({ admin: "/api/admin/ai/interactions",             public: "/api/ai/interactions" });

/* ===================== MORE ENTITIES (batch add) ===================== */
/* Accounting / AP & Banking */
export const Payable         = mkSimpleEntity({ admin: "/api/admin/accounting/payables",         public: "/api/accounting/payables" });
export const Vendor          = mkSimpleEntity({ admin: "/api/admin/accounting/vendors",          public: "/api/accounting/vendors" });
export const BankAccount     = mkSimpleEntity({ admin: "/api/admin/accounting/bank-accounts",    public: "/api/accounting/bank-accounts" });
export const BankTransaction = mkSimpleEntity({ admin: "/api/admin/accounting/bank-transactions",public: "/api/accounting/bank-transactions" });
export const JournalEntry    = mkSimpleEntity({ admin: "/api/admin/accounting/journal-entries",  public: "/api/accounting/journal-entries" });

// General Ledger
export const LedgerEntry   = mkSimpleEntity({ admin: "/api/admin/accounting/ledger-entries",  public: "/api/accounting/ledger-entries" });
export const LedgerAccount = mkSimpleEntity({ admin: "/api/admin/accounting/ledger-accounts", public: "/api/accounting/ledger-accounts" });

/* Safety (runs) */
export const SafetyChecklistRun = mkSimpleEntity({ admin: "/api/admin/safety/checklist-runs",    public: "/api/safety/checklist-runs" });
export const ChecklistRun = SafetyChecklistRun;

/* Housekeeping & Accommodation ops */
export const Housekeeping    = mkSimpleEntity({ admin: "/api/admin/accommodations/housekeeping", public: "/api/accommodations/housekeeping" });

/* Reciprocal & guest access */
export const GuestPass       = mkSimpleEntity({ admin: "/api/admin/reciprocal/guest-passes",     public: "/api/reciprocal/guest-passes" });

/* Email / Campaigns */
export const EmailCampaign   = mkSimpleEntity({ admin: "/api/admin/email/campaigns",             public: "/api/email/campaigns" });

/* ClubVision / AI planning */
export const ClubVisionEventIdea     = mkSimpleEntity({ admin: "/api/admin/clubvision/event-ideas",       public: "/api/clubvision/event-ideas" });
export const ClubVisionMultiDomainRun= mkSimpleEntity({ admin: "/api/admin/clubvision/multi-domain/runs", public: "/api/clubvision/multi-domain/runs" });
export const BudgetStory             = mkSimpleEntity({ admin: "/api/admin/budgeting/stories",            public: "/api/budgeting/stories" });

/* Polo (static, de-duped) */
export const PoloStable = {
  async list(params = {}) {
    return await tryGet([poloClubPath("/polo/stables")], params);
  },
  async create(payload) {
    return await tryPost([poloClubPath("/polo/stables")], payload);
  },
  async update(id, body) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPut([poloClubPath(`/polo/stables/${pid}`)], body);
  },
  async delete(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryDelete([poloClubPath(`/polo/stables/${pid}`)]);
  },
};

export const PoloStall = {
  async listForStable(stableId, params = {}) {
    if (!stableId) return [];
    const sid = encodeURIComponent(String(stableId));
    return await tryGet([poloClubPath(`/polo/stables/${sid}/stalls`)], params);
  },
  async createForStable(stableId, payload) {
    if (!stableId) return null;
    const sid = encodeURIComponent(String(stableId));
    return await tryPost([poloClubPath(`/polo/stables/${sid}/stalls`)], payload);
  },
  async update(id, payload) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPut([poloClubPath(`/polo/stalls/${pid}`)], payload);
  },
  async delete(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryDelete([poloClubPath(`/polo/stalls/${pid}`)]);
  },
};

export const PoloHorse = {
  async list(params = {}) {
    return await tryGet([poloClubPath("/polo/horses")], params);
  },
  async create(payload) {
    return await tryPost([poloClubPath("/polo/horses")], payload);
  },
  async update(id, body) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPut([poloClubPath(`/polo/horses/${pid}`)], body);
  },
  async delete(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryDelete([poloClubPath(`/polo/horses/${pid}`)]);
  },
};

export const PoloField = {
  async list(params = {}) {
    return await tryGet([poloClubPath("/polo/fields")], params);
  },
  async get(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryGet([poloClubPath(`/polo/fields/${pid}`)]);
  },
  async create(payload) {
    return await tryPost([poloClubPath("/polo/fields")], payload);
  },
  async update(id, body) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPatch([poloClubPath(`/polo/fields/${pid}`)], body);
  },
  async delete(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryDelete([poloClubPath(`/polo/fields/${pid}`)]);
  },
};

export const PoloString = {
  async list(params = {}) {
    return await tryGet([poloClubPath("/polo/strings")], params);
  },
  async create(payload) {
    return await tryPost([poloClubPath("/polo/strings")], payload);
  },
  async update(id, body) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPut([poloClubPath(`/polo/strings/${pid}`)], body);
  },
  async delete(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryDelete([poloClubPath(`/polo/strings/${pid}`)]);
  },
};

export const PoloStringHorse = {
  async listForString(stringId, params = {}) {
    if (!stringId) return [];
    const sid = encodeURIComponent(String(stringId));
    return await tryGet([poloClubPath(`/polo/strings/${sid}/horses`)], params);
  },
  async addToString(stringId, payload) {
    if (!stringId) return null;
    const sid = encodeURIComponent(String(stringId));
    return await tryPost([poloClubPath(`/polo/strings/${sid}/horses`)], payload);
  },
  async update(id, payload) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPut([poloClubPath(`/polo/string-horses/${pid}`)], payload);
  },
  async delete(stringId, stringHorseId) {
    if (!stringId || !stringHorseId) return null;
    const sid = encodeURIComponent(String(stringId));
    const shid = encodeURIComponent(String(stringHorseId));
    return await tryDelete([poloClubPath(`/polo/strings/${sid}/horses/${shid}`)]);
  },
};

export const PoloUploads = {
  async presign(payload) {
    return await tryPost([poloClubPath("/polo/uploads/presign")], payload);
  },
};

export const PoloWaivers = {
  async list(params = {}) {
    return await tryGet([poloClubPath("/polo/waivers")], params);
  },
  async create(payload) {
    const path = poloClubPath("/polo/waivers");
    if (!path) return null;
    return await apiPost(path, payload);
  },
  async view(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryGet([poloClubPath(`/polo/waivers/${pid}/view`)]);
  },
  async download(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryGet([poloClubPath(`/polo/waivers/${pid}/download`)]);
  },
  async presign(id, payload) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPost([poloClubPath(`/polo/waivers/${pid}/presign`)], payload);
  },
  async uploadComplete(id, payload) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPost([poloClubPath(`/polo/waivers/${pid}/upload-complete`)], payload);
  },
  async update(id, payload) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPatch([poloClubPath(`/polo/waivers/${pid}`)], payload);
  },
  async delete(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryDelete([poloClubPath(`/polo/waivers/${pid}`)]);
  },
};

export const PoloMatches = {
  async list(params = {}) {
    return await tryGet([poloClubPath("/polo/matches")], params);
  },
  async matchDay(params = {}) {
    return await tryGet([poloClubPath("/polo/match-day")], params);
  },
  async create(payload) {
    const path = poloClubPath("/polo/matches");
    if (!path) return null;
    return await apiPost(path, payload);
  },
  async get(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryGet([poloClubPath(`/polo/matches/${pid}`)]);
  },
  async update(id, payload) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPatch([poloClubPath(`/polo/matches/${pid}`)], payload);
  },
  async delete(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryDelete([poloClubPath(`/polo/matches/${pid}`)]);
  },
  async setStatus(id, status) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPost([poloClubPath(`/polo/matches/${pid}/status`)], { status });
  },
  async listScoreEvents(matchId) {
    if (!matchId) return null;
    const pid = encodeURIComponent(String(matchId));
    return await tryGet([poloClubPath(`/polo/matches/${pid}/score-events`)]);
  },
  async addScoreEvent(matchId, payload) {
    if (!matchId) return null;
    const pid = encodeURIComponent(String(matchId));
    return await tryPost([poloClubPath(`/polo/matches/${pid}/score-events`)], payload);
  },
  async updateScoreEvent(matchId, eventId, payload) {
    if (!matchId || !eventId) return null;
    const pid = encodeURIComponent(String(matchId));
    const eid = encodeURIComponent(String(eventId));
    return await tryPatch([poloClubPath(`/polo/matches/${pid}/score-events/${eid}`)], payload);
  },
  async deleteScoreEvent(matchId, eventId) {
    if (!matchId || !eventId) return null;
    const pid = encodeURIComponent(String(matchId));
    const eid = encodeURIComponent(String(eventId));
    return await tryDelete([poloClubPath(`/polo/matches/${pid}/score-events/${eid}`)]);
  },
  async listOfficials(matchId) {
    if (!matchId) return null;
    const pid = encodeURIComponent(String(matchId));
    return await tryGet([poloClubPath(`/polo/matches/${pid}/officials`)]);
  },
  async setOfficials(matchId, officials) {
    if (!matchId) return null;
    const pid = encodeURIComponent(String(matchId));
    return await tryPut([poloClubPath(`/polo/matches/${pid}/officials`)], {
      officials: Array.isArray(officials) ? officials : [],
    });
  },
  async listNotes(matchId) {
    if (!matchId) return null;
    const pid = encodeURIComponent(String(matchId));
    return await tryGet([poloClubPath(`/polo/matches/${pid}/notes`)]);
  },
  async addNote(matchId, payload) {
    if (!matchId) return null;
    const pid = encodeURIComponent(String(matchId));
    return await tryPost([poloClubPath(`/polo/matches/${pid}/notes`)], payload);
  },
};

export const PoloStaff = {
  async list(params = {}) {
    return await tryGet([poloClubPath("/polo/staff")], params);
  },
};

export const PoloStaffingAssignments = {
  async list(params = {}) {
    return await tryGet([poloClubPath("/polo/staffing-assignments")], params);
  },
  async create(payload) {
    return await tryPost([poloClubPath("/polo/staffing-assignments")], payload);
  },
  async update(id, payload) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPatch([poloClubPath(`/polo/staffing-assignments/${pid}`)], payload);
  },
  async delete(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryDelete([poloClubPath(`/polo/staffing-assignments/${pid}`)]);
  },
};

export const PoloStaffing = mkSimpleEntity({
  admin: "/api/admin/polo/staffing",
  public: "/api/polo/staffing",
  alt: ["/api/admin/polo/staff", "/api/polo/staff", "/api/admin/polo/assignments", "/api/polo/assignments"],
});

export const PoloEventsOps = {
  async list(params = {}) {
    return await tryGet([poloClubPath("/polo/ops-events")], params);
  },
  async get(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryGet([poloClubPath(`/polo/ops-events/${pid}`)]);
  },
  async create(payload) {
    return await tryPost([poloClubPath("/polo/ops-events")], payload);
  },
  async update(id, body) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryPatch([poloClubPath(`/polo/ops-events/${pid}`)], body);
  },
  async delete(id) {
    if (!id) return null;
    const pid = encodeURIComponent(String(id));
    return await tryDelete([poloClubPath(`/polo/ops-events/${pid}`)]);
  },
};

/* Racquet */
export const RacquetBooking  = mkSimpleEntity({ admin: "/api/admin/racquet/bookings",            public: "/api/racquet/bookings" });

/* Weddings */
export const WeddingPipelineStage = mkSimpleEntity({ admin: "/api/admin/weddings/pipeline-stages", public: "/api/weddings/pipeline-stages" });

/* POS / Inventory integrations */
export const POSIntegration    = mkSimpleEntity({ admin: "/api/admin/pos/integrations",         public: "/api/pos/integrations" });
export const POSItem           = mkSimpleEntity({ admin: "/api/admin/pos/items",                public: "/api/pos/items" });
export const InventorySyncLog  = mkSimpleEntity({ admin: "/api/admin/inventory/sync-logs",      public: "/api/inventory/sync-logs" });
export const POSSyncEvent      = mkSimpleEntity({ admin: "/api/admin/pos/sync-events",          public: "/api/pos/sync-events" });

/* Social */
export const SocialPost     = mkSimpleEntity({ admin: "/api/admin/social/posts",                public: "/api/social/posts" });
export const SocialGroup    = mkSimpleEntity({ admin: "/api/admin/social/groups",               public: "/api/social/groups" });
export const SocialComment  = mkSimpleEntity({ admin: "/api/admin/social/comments",             public: "/api/social/comments" });
export const Conversation   = mkSimpleEntity({ admin: "/api/admin/messages/conversations",      public: "/api/messages/conversations" });
export const Inbox = {
  async listConversations(params = {}) {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/conversations`
      : null;

    return await tryGet([clubPath, "/api/inbox/conversations"], params);
  },
  async listMessages(conversationId, params = {}) {
    if (!conversationId) return [];
    const cid = encodeURIComponent(String(conversationId));
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/conversations/${cid}/messages`
      : null;

    return await tryGet([clubPath, `/api/inbox/conversations/${cid}/messages`], params);
  },
  async sendMessage(conversationId, payload) {
    if (!conversationId) return null;
    const cid = encodeURIComponent(String(conversationId));
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/conversations/${cid}/messages`
      : null;

    return await tryPost([clubPath, `/api/inbox/conversations/${cid}/messages`], payload);
  },
  async markRead(conversationId) {
    if (!conversationId) return null;
    const cid = encodeURIComponent(String(conversationId));
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/conversations/${cid}/read`
      : null;

    return await tryPost([clubPath, `/api/inbox/conversations/${cid}/read`], {});
  },
  async createChannel(payload) {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/channels`
      : null;

    return await tryPost([clubPath, `/api/inbox/channels`], payload);
  },
  async createDM(other_user_id) {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/dms`
      : null;

    const payload = typeof other_user_id === "object" ? other_user_id : { other_user_id };
    return await tryPost([clubPath, `/api/inbox/dms`], payload);
  },
  async ensureDM(otherUserId) {
    const normalized = String(otherUserId || '').trim();
    if (!normalized) return null;
    return this.createDM({ other_user_id: normalized });
  },
  async listStaff() {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/staff`
      : null;

    return await tryGet([clubPath]);
  },
  async unreadSummary(clubCodeOverride = null) {
    const clubCode = clubCodeOverride || getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/unread-summary`
      : null;

    return await tryGet([clubPath]);
  },
  async listEvents(since, clubCodeOverride = null) {
    const clubCode = clubCodeOverride || getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/events?since=${encodeURIComponent(
          since || new Date().toISOString()
        )}`
      : null;

    return await tryGet([clubPath]);
  },
  async getSettings() {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/settings`
      : null;

    return await tryGet([clubPath]);
  },
  async updateSettings(payload = {}) {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/settings`
      : null;

    return await tryPut([clubPath], payload);
  },


  async smartEmailSettings() {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/settings/smart`
      : null;
    return await tryGet([clubPath]);
  },
  async updateSmartEmailSettings(payload = {}) {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/settings/smart`
      : null;
    return await tryPut([clubPath], payload);
  },
  async updateSmartEmailCategory(categoryKey, payload = {}) {
    if (!categoryKey) return null;
    const clubCode = getActiveClubCode();
    const key = encodeURIComponent(String(categoryKey));
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/settings/smart/categories/${key}`
      : null;
    return await tryPut([clubPath], payload);
  },
  async reclassifySmartEmails(payload = {}) {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/settings/smart/reclassify`
      : null;
    return await tryPost([clubPath], payload);
  },
  async emailStatus() {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/status`
      : null;
    const response = await tryGet([clubPath]);
    const payload = response?.data ?? response;
    if (payload && typeof payload === 'object') {
      return {
        ...payload,
        auth_status: payload.auth_status ?? null,
        last_auth_error: payload.last_auth_error ?? null,
      };
    }
    return response;
  },
  async currentStaffEmailStatus() {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/status?scope=current_staff`
      : null;
    const response = await tryGet([clubPath]);
    const payload = response?.data ?? response;
    if (payload && typeof payload === 'object') {
      return {
        ...payload,
        auth_status: payload.auth_status ?? null,
        last_auth_error: payload.last_auth_error ?? null,
      };
    }
    return response;
  },
  async emailMessages(params = {}) {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/messages`
      : null;
    return await tryGet([clubPath], params);
  },
  async refreshEmailFolder(folder = 'inbox') {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/refresh`
      : null;
    return await tryPost([clubPath], { folder });
  },
  async emailSmartGroups(params = {}) {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/messages/smart-groups`
      : null;
    return await tryGet([clubPath], params);
  },
  async emailMessageDetail(messageId, params = {}) {
    if (!messageId) return null;
    const clubCode = getActiveClubCode();
    const mid = encodeURIComponent(String(messageId));
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/messages/${mid}`
      : null;
    return await tryGet([clubPath], params);
  },
  async emailSignature() {
    const clubCode = getActiveClubCode();
    return await tryGet([clubCode ? `/api/clubs/${encodeURIComponent(clubCode)}/email/signature` : null]);
  },
  async saveEmailSignature(payload) {
    const clubCode = getActiveClubCode();
    return await tryPut([clubCode ? `/api/clubs/${encodeURIComponent(clubCode)}/email/signature` : null], payload);
  },
  async uploadEmailSignatureImage(file) {
    const clubCode = getActiveClubCode();
    const body = new FormData();
    body.append('image', file);
    return await tryPost([clubCode ? `/api/clubs/${encodeURIComponent(clubCode)}/email/signature/assets` : null], body);
  },
  async removeEmailSignature() {
    const clubCode = getActiveClubCode();
    return await tryDelete([clubCode ? `/api/clubs/${encodeURIComponent(clubCode)}/email/signature` : null]);
  },
  async sendEmail(payload) {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/send`
      : null;
    return await tryPost([clubPath], payload);
  },
  async emailReply(messageId, payload = {}) {
    if (!messageId) return null;
    const clubCode = getActiveClubCode();
    const mid = encodeURIComponent(String(messageId));
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/messages/${mid}/reply`
      : null;
    return await tryPost([clubPath], payload);
  },
  async emailDraftReply(messageId, payload = {}) {
    if (!messageId) return null;
    const clubCode = getActiveClubCode();
    const mid = encodeURIComponent(String(messageId));
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/messages/${mid}/draft-reply`
      : null;
    return await tryPost([clubPath], payload);
  },
  async emailForward(messageId, payload = {}) {
    if (!messageId) return null;
    const clubCode = getActiveClubCode();
    const mid = encodeURIComponent(String(messageId));
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/messages/${mid}/forward`
      : null;
    return await tryPost([clubPath], payload);
  },
  async emailThreadedSend(payload = {}) {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/messages/threaded-send`
      : null;
    return await tryPost([clubPath], payload);
  },
  async emailFlag(messageId, payload = {}) {
    if (!messageId) return null;
    const clubCode = getActiveClubCode();
    const mid = encodeURIComponent(String(messageId));
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/messages/${mid}/flag`
      : null;
    return await tryPatch([clubPath], payload);
  },
  async emailReadState(messageId, payload = {}) {
    if (!messageId) return null;
    const clubCode = getActiveClubCode();
    const mid = encodeURIComponent(String(messageId));
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/email/messages/${mid}/read-state`
      : null;
    return await tryPatch([clubPath], payload);
  },
  async emailPin(messageId, payload = {}) {
    if (!messageId) return null;
    const clubCode = getActiveClubCode();
    const clubPath = clubCode ? `/api/clubs/${encodeURIComponent(clubCode)}/email/messages/${encodeURIComponent(String(messageId))}/pin` : null;
    return await tryPatch([clubPath], payload);
  },
  async emailDelete(messageId) {
    if (!messageId) return null;
    const clubCode = getActiveClubCode();
    const clubPath = clubCode ? `/api/clubs/${encodeURIComponent(clubCode)}/email/messages/${encodeURIComponent(String(messageId))}` : null;
    return await tryDelete([clubPath]);
  },
  async startMicrosoftEmailConnect(clubCodeOverride = null) {
    const clubCode = clubCodeOverride || getActiveClubCode();
    if (!clubCode) {
      throw new Error('club_code_required');
    }

    const path = `/api/clubs/${encodeURIComponent(clubCode)}/integrations/microsoft/start`;
    // Use the same TokenBridge-backed request as every other Portal API.  In
    // particular, do not turn this into a cookie/session-only OAuth endpoint.
    const response = await apiStartMicrosoftOAuth(path);
    if (!response?.ok || !response?.data?.authorizeUrl) {
      return response;
    }
    return response.data.authorizeUrl;
  },
  async listNotifications(params = {}) {
    const clubCode = getActiveClubCode();
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', params.limit);
    if (params.status) query.set('status', params.status);
    if (params.includeRead || params.include_read) query.set('include_read', 'true');
    const suffix = query.toString();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/notifications${suffix ? `?${suffix}` : ''}`
      : null;

    return await tryGet([clubPath]);
  },
  async getNotification(notificationId) {
    if (!notificationId) return null;
    const clubCode = getActiveClubCode();
    const nid = encodeURIComponent(String(notificationId));
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/notifications/${nid}`
      : null;

    return await tryGet([clubPath]);
  },
  async markNotificationRead(notificationId) {
    if (!notificationId) return null;
    const clubCode = getActiveClubCode();
    const nid = encodeURIComponent(String(notificationId));
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/notifications/${nid}/read`
      : null;

    return await tryPatch([clubPath], {});
  },
  async markNotificationsRead(ids) {
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/notifications/read`
      : null;

    return await tryPost([clubPath], { ids });
  },
  async listChannelMembers(conversationId) {
    if (!conversationId) return [];
    const cid = encodeURIComponent(String(conversationId));
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/channels/${cid}/members`
      : null;

    return await tryGet([clubPath]);
  },
  async joinChannel(conversationId) {
    if (!conversationId) return null;
    const cid = encodeURIComponent(String(conversationId));
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/channels/${cid}/join`
      : null;

    return await tryPost([clubPath], {});
  },
  async leaveChannel(conversationId) {
    if (!conversationId) return null;
    const cid = encodeURIComponent(String(conversationId));
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/channels/${cid}/leave`
      : null;

    return await tryPost([clubPath], {});
  },
  async addChannelMembers(conversationId, user_ids = []) {
    if (!conversationId) return null;
    const cid = encodeURIComponent(String(conversationId));
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/channels/${cid}/members`
      : null;

    return await tryPost([clubPath], { user_ids });
  },
  async removeChannelMember(conversationId, userId) {
    if (!conversationId || !userId) return null;
    const cid = encodeURIComponent(String(conversationId));
    const uid = encodeURIComponent(String(userId));
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/channels/${cid}/members/${uid}`
      : null;

    return await tryDelete([clubPath]);
  },
  async setChannelMemberRole(conversationId, userId, role) {
    if (!conversationId || !userId || !role) return null;
    const cid = encodeURIComponent(String(conversationId));
    const uid = encodeURIComponent(String(userId));
    const clubCode = getActiveClubCode();
    const clubPath = clubCode
      ? `/api/clubs/${encodeURIComponent(clubCode)}/inbox/channels/${cid}/members/${uid}/role`
      : null;

    return await tryPost([clubPath], { role });
  },
};

/* Privana Web (site builder) */
export const WebsiteProject = mkSimpleEntity({ admin: "/api/admin/website/projects",            public: "/api/website/projects" });

/* Mobile / Members app */
export const MobileAppEvent = mkSimpleEntity({ admin: "/api/admin/mobile/events",               public: "/api/mobile/events" });

/* Fantasy golf */
export const FantasyTeam    = mkSimpleEntity({ admin: "/api/admin/fantasy/teams",               public: "/api/fantasy/teams" });

/* Spa */
export const SpaService     = mkSimpleEntity({ admin: "/api/admin/spa/services",                public: "/api/spa/services" });
export const SpaBooking     = mkSimpleEntity({ admin: "/api/admin/spa/bookings",                public: "/api/spa/bookings" });

/* Competitions / contests */
export const Competition        = mkSimpleEntity({ admin: "/api/admin/competitions",            public: "/api/competitions" });
export const CompetitionEntry   = mkSimpleEntity({ admin: "/api/admin/competitions/entries",    public: "/api/competitions/entries" });
export const CompetitionScore   = mkSimpleEntity({ admin: "/api/admin/competitions/scores",     public: "/api/competitions/scores" });
export const OnCourseContest    = mkSimpleEntity({ admin: "/api/admin/competitions/contests",   public: "/api/competitions/contests" });
export const FantasyConfig      = mkSimpleEntity({ admin: "/api/admin/fantasy/config",          public: "/api/fantasy/config" });

/* ================================= ROOM BOOKING =============================== */
export const RoomBooking = {
  // Global
  async list(params = {}) {
    return await tryGet(
      [
        "/api/admin/accommodations/bookings",
        "/api/admin/rooms/bookings",
        "/api/rooms/bookings",
        "/api/accommodations/bookings",
      ],
      params
    );
  },
  async get(id) {
    const bid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/admin/accommodations/bookings/${bid}`,
        `/api/admin/rooms/bookings/${bid}`,
        `/api/rooms/bookings/${bid}`,
        `/api/accommodations/bookings/${bid}`,
      ]
    );
  },
  async create(payload) {
    return await tryPost(
      [
        "/api/admin/accommodations/bookings",
        "/api/admin/rooms/bookings",
        "/api/rooms/bookings",
        "/api/accommodations/bookings",
      ],
      payload
    );
  },
  async update(id, payload) {
    const bid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/accommodations/bookings/${bid}`,
        `/api/admin/rooms/bookings/${bid}`,
        `/api/rooms/bookings/${bid}`,
        `/api/accommodations/bookings/${bid}`,
      ],
      payload
    );
  },
  async delete(id) {
    const bid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/admin/accommodations/bookings/${bid}`,
        `/api/admin/rooms/bookings/${bid}`,
        `/api/rooms/bookings/${bid}`,
        `/api/accommodations/bookings/${bid}`,
      ]
    );
  },

  // Helpers
  async availability(params = {}) {
    return await tryGet(
      [
        "/api/admin/accommodations/bookings/availability",
        "/api/admin/rooms/bookings/availability",
        "/api/rooms/bookings/availability",
        "/api/accommodations/bookings/availability",
      ],
      params
    );
  },

  // Club-scoped
  async listForClub(clubRef, params = {}) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/accommodations/bookings`,
        `/api/admin/clubs/${cr}/rooms/bookings`,
        `/api/clubs/${cr}/accommodations/bookings`,
      ],
      params
    );
  },
  async getForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const bid = encodeURIComponent(String(id));
    return await tryGet(
      [
        `/api/admin/clubs/${cr}/accommodations/bookings/${bid}`,
        `/api/admin/clubs/${cr}/rooms/bookings/${bid}`,
        `/api/clubs/${cr}/accommodations/bookings/${bid}`,
      ]
    );
  },
  async createForClub(clubRef, payload) {
    const cr = encodeURIComponent(String(clubRef));
    return await tryPost(
      [
        `/api/admin/clubs/${cr}/accommodations/bookings`,
        `/api/admin/clubs/${cr}/rooms/bookings`,
        `/api/clubs/${cr}/accommodations/bookings`,
      ],
      payload
    );
  },
  async updateForClub(clubRef, id, payload) {
    const cr = encodeURIComponent(String(clubRef));
    const bid = encodeURIComponent(String(id));
    return await tryPatch(
      [
        `/api/admin/clubs/${cr}/accommodations/bookings/${bid}`,
        `/api/admin/clubs/${cr}/rooms/bookings/${bid}`,
        `/api/clubs/${cr}/accommodations/bookings/${bid}`,
      ],
      payload
    );
  },
  async deleteForClub(clubRef, id) {
    const cr = encodeURIComponent(String(clubRef));
    const bid = encodeURIComponent(String(id));
    return await tryDelete(
      [
        `/api/admin/clubs/${cr}/accommodations/bookings/${bid}`,
        `/api/admin/clubs/${cr}/rooms/bookings/${bid}`,
        `/api/clubs/${cr}/accommodations/bookings/${bid}`,
      ]
    );
  },
};

/* Year-End close (accounting) */
export const YearEndClosure = mkSimpleEntity({
  admin: "/api/admin/accounting/year-end-closures",
  public: "/api/accounting/year-end-closures",
  // If your backend used a different naming, these alternates will still work:
  alt: [
    "/api/admin/accounting/year-end",
    "/api/admin/accounting/yearend",
    "/api/accounting/year-end",
    "/api/accounting/yearend",
  ],
});

// Builder for user-defined custom entities (used by CustomEntities.jsx)
export const CustomEntity = mkSimpleEntity({
  admin: "/api/admin/custom-entities",
  public: "/api/custom-entities",
  // alternates in case your backend uses a different slug
  alt: [
    "/api/admin/custom/entities",
    "/api/custom/entities",
    "/api/admin/entities/custom",
    "/api/entities/custom",
  ],
});

// On-the-fly factory: create an entity client for an arbitrary collection slug
export const customEntityBySlug = (slug) =>
  mkSimpleEntity({
    admin: `/api/admin/${slug}`,
    public: `/api/${slug}`,
  });

/* ======================= CATCH-ALL REMAINING ENTITIES ======================= */
/* Access control */
export const AccessAlert    = mkSimpleEntity({ admin: "/api/admin/access/alerts",     public: "/api/access/alerts" });
export const AccessLog      = mkSimpleEntity({ admin: "/api/admin/access/logs",       public: "/api/access/logs" });
export const AccessRequest  = mkSimpleEntity({ admin: "/api/admin/access/requests",   public: "/api/access/requests" });

/* Activities / programming */
export const Activity       = mkSimpleEntity({ admin: "/api/admin/activities",        public: "/api/activities" });
export const ActivityBooking= mkSimpleEntity({ admin: "/api/admin/activities/bookings", public: "/api/activities/bookings" });

/* Analytics / planning */
export const ActualsCache   = mkSimpleEntity({ admin: "/api/admin/analytics/actuals-cache", public: "/api/analytics/actuals-cache" });
export const CRMInsight     = mkSimpleEntity({ admin: "/api/admin/crm/insights",     public: "/api/crm/insights" });

/* Documents / board */
export const BoardDocument  = mkSimpleEntity({ admin: "/api/admin/board/documents",   public: "/api/board/documents" });

/* Security / cameras */
export const CameraEvent    = mkSimpleEntity({ admin: "/api/admin/security/camera-events", public: "/api/security/camera-events" });

/* Camps */
export const Camp           = mkSimpleEntity({ admin: "/api/admin/camps",             public: "/api/camps" });
export const CampEnrollment = mkSimpleEntity({ admin: "/api/admin/camps/enrollments", public: "/api/camps/enrollments" });

/* Clubs / partners */
export const ClubPartner    = mkSimpleEntity({ admin: "/api/admin/clubs/partners",    public: "/api/clubs/partners" });

/* ClubVision extras */
export const ClubVisionLiveEventForecast = mkSimpleEntity({ admin: "/api/admin/clubvision/live-event-forecasts", public: "/api/clubvision/live-event-forecasts" });
export const ClubVisionRecommendation    = mkSimpleEntity({ admin: "/api/admin/clubvision/recommendations",      public: "/api/clubvision/recommendations" });
export const ClubVisionResult            = mkSimpleEntity({ admin: "/api/admin/clubvision/results",             public: "/api/clubvision/results" });
export const ClubVisionRiskSignal        = mkSimpleEntity({ admin: "/api/admin/clubvision/risk-signals",        public: "/api/clubvision/risk-signals" });
export const ClubVisionScenario          = mkSimpleEntity({ admin: "/api/admin/clubvision/scenarios",           public: "/api/clubvision/scenarios" });

/* Contracts / finance structure */
export const ContractRenewal = mkSimpleEntity({ admin: "/api/admin/contracts/renewals", public: "/api/contracts/renewals" });
export const CostCenter      = mkSimpleEntity({ admin: "/api/admin/accounting/cost-centers", public: "/api/accounting/cost-centers" });

/* Generic course entity (separate from GolfCourse) */
export const Course         = mkSimpleEntity({ admin: "/api/admin/courses",           public: "/api/courses" });

/* Email events (deliverability, opens/clicks) */
export const EmailEvent     = mkSimpleEntity({ admin: "/api/admin/email/events",      public: "/api/email/events" });

/* Golf extras */
export const GolfMajor      = mkSimpleEntity({ admin: "/api/admin/golf/majors",       public: "/api/golf/majors" });

/* Instruction / lessons */
export const Instructor     = mkSimpleEntity({ admin: "/api/admin/instructors",       public: "/api/instructors" });
export const Lesson         = mkSimpleEntity({ admin: "/api/admin/lessons",           public: "/api/lessons" });
export const LessonProgress = mkSimpleEntity({ admin: "/api/admin/lessons/progress",  public: "/api/lessons/progress" });
export const Leaderboard    = mkSimpleEntity({ admin: "/api/admin/leaderboards",      public: "/api/leaderboards" });
export const LeadSource     = mkSimpleEntity({ admin: "/api/admin/crm/lead-sources",  public: "/api/crm/lead-sources" });

/* Marina */
export const MarinaBooking  = mkSimpleEntity({ admin: "/api/admin/marina/bookings",   public: "/api/marina/bookings" });
export const MarinaSlip     = mkSimpleEntity({ admin: "/api/admin/marina/slips",      public: "/api/marina/slips" });

/* Meetings */
export const Meeting        = mkSimpleEntity({ admin: "/api/admin/meetings",          public: "/api/meetings" });

/* Sensors / motion */
export const Motion         = mkSimpleEntity({ admin: "/api/admin/iot/motion",        public: "/api/iot/motion" });

/* Permissions / RBAC */
export const Permission     = mkSimpleEntity({ admin: "/api/admin/permissions",       public: "/api/permissions" });
export const ZonePermission = mkSimpleEntity({ admin: "/api/admin/permissions/zones", public: "/api/permissions/zones" });

/* Pricing / procurement analytics */
export const PriceIndex         = mkSimpleEntity({ admin: "/api/admin/procurement/price-index", public: "/api/procurement/price-index" });
export const ProcurementHeatmap = mkSimpleEntity({ admin: "/api/admin/procurement/heatmap",     public: "/api/procurement/heatmap" });

/* RFQ extras */
export const RFQSubmission  = mkSimpleEntity({ admin: "/api/admin/procurement/rfq-submissions", public: "/api/procurement/rfq-submissions" });

/* Suppliers */
export const SupplierOrder  = mkSimpleEntity({ admin: "/api/admin/procurement/supplier-orders", public: "/api/procurement/supplier-orders" });

/* Visitors / waivers */
export const Visitor        = mkSimpleEntity({ admin: "/api/admin/visitors",          public: "/api/visitors" });
export const Waiver         = mkSimpleEntity({ admin: "/api/admin/waivers",           public: "/api/waivers" });

/* Website media */
export const WebsiteMedia   = mkSimpleEntity({ admin: "/api/admin/website/media",     public: "/api/website/media" });

/* Weddings (additional) */
export const WeddingAddOn       = mkSimpleEntity({ admin: "/api/admin/weddings/add-ons",     public: "/api/weddings/add-ons" });
export const WeddingDatePrice   = mkSimpleEntity({ admin: "/api/admin/weddings/date-prices", public: "/api/weddings/date-prices" });
export const WeddingPackage     = mkSimpleEntity({ admin: "/api/admin/weddings/packages",    public: "/api/weddings/packages" });
export const WeddingProposal    = mkSimpleEntity({ admin: "/api/admin/weddings/proposals",   public: "/api/weddings/proposals" });
export const WeddingSpace       = mkSimpleEntity({ admin: "/api/admin/weddings/spaces",      public: "/api/weddings/spaces" });
export const WeddingViewing     = mkSimpleEntity({ admin: "/api/admin/weddings/viewings",    public: "/api/weddings/viewings" });

/* --------------------------- keep Club ------------------------ */
export { Club } from "./entities/Club";

export default {};
